<template>
  <div class="new-form-renderer" :class="{ 'is-readonly': readonly }">
    <div v-if="!controls || controls.length === 0" class="empty-controls">
      <i class="fas fa-inbox"></i>
      <span>表单未配置控件</span>
    </div>
    <div v-else class="form-grid">
      <div
        v-for="ctrl in visibleControls"
        :key="ctrl.id"
        class="form-cell"
        :style="getCellStyle(ctrl)"
      >
        <!-- Input -->
        <div v-if="ctrl.type === 'Input'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <div class="input-with-browse" v-if="ctrl.props.autobrowse">
            <input type="text" class="ctrl-field browse-input"
              :value="getFieldValue(ctrl)" :placeholder="ctrl.props.placeholder || ''"
              :readonly="readonly || ctrl.props.readonly" :disabled="ctrl.props.disabled"
              :maxlength="ctrl.props.maxLength"
              :style="{ textAlign: ctrl.props.align || 'left' }"
              @input="onFieldInput(ctrl, $event.target.value)"
              @blur="onInputBlur(ctrl, $event.target.value)" />
            <button type="button" class="browse-btn" :disabled="readonly || ctrl.props.disabled" @click="openLookup(ctrl)">
              <i class="fas fa-search"></i>
            </button>
          </div>
          <input v-else type="text" class="ctrl-field"
            :value="getFieldValue(ctrl)" :placeholder="ctrl.props.placeholder || ''"
            :readonly="readonly || ctrl.props.readonly" :disabled="ctrl.props.disabled"
            :maxlength="ctrl.props.maxLength"
            :style="{ textAlign: ctrl.props.align || 'left' }"
            @input="onFieldInput(ctrl, $event.target.value)" />
          <span v-if="ctrl.props.iscapital && getFieldValue(ctrl)" class="capital-hint">大写：{{ toChineseCapital(getFieldValue(ctrl)) }}</span>
          <span v-if="ctrl.props.ispercent && ctrl.props.total" class="percent-hint">{{ computePercent(ctrl) }}</span>
        </div>

        <!-- Textarea -->
        <div v-else-if="ctrl.type === 'Textarea'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <textarea class="ctrl-field" :rows="ctrl.props.rows || 4"
            :value="getFieldValue(ctrl)" :placeholder="ctrl.props.placeholder || ''"
            :readonly="readonly || ctrl.props.readonly" :disabled="ctrl.props.disabled"
            :maxlength="ctrl.props.maxLength"
            @input="onFieldInput(ctrl, $event.target.value)"
            @blur="executeEvent(ctrl, 'onBlur', { value: $event.target.value, field: getFieldName(ctrl) })"></textarea>
        </div>

        <!-- InputNumber -->
        <div v-else-if="ctrl.type === 'InputNumber'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <input type="number" class="ctrl-field"
            :value="getFieldValue(ctrl)" :placeholder="ctrl.props.placeholder || ''"
            :min="ctrl.props.neg ? 0 : ctrl.props.min" :max="ctrl.props.max"
            :step="Math.pow(10, -(ctrl.props.decimal || 0))"
            :style="{ textAlign: ctrl.props.align || 'left' }"
            :readonly="readonly || ctrl.props.readonly" :disabled="ctrl.props.disabled"
            @input="onFieldInput(ctrl, $event.target.value)"
            @blur="executeEvent(ctrl, 'onBlur', { value: $event.target.value, field: getFieldName(ctrl) })" />
          <span v-if="ctrl.props.separator && getFieldValue(ctrl)" class="num-hint">千分位：{{ formatNumber(getFieldValue(ctrl), ctrl.props.decimal || 0, true) }}</span>
          <span v-if="ctrl.props.total" class="num-hint">表达式：{{ computeExpression(ctrl) }}</span>
        </div>

        <!-- Password -->
        <div v-else-if="ctrl.type === 'Password'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <input type="password" class="ctrl-field"
            :value="getFieldValue(ctrl)" :placeholder="ctrl.props.placeholder || ''"
            :readonly="readonly || ctrl.props.readonly" :disabled="ctrl.props.disabled"
            @input="onFieldInput(ctrl, $event.target.value)" />
        </div>

        <!-- Email -->
        <div v-else-if="ctrl.type === 'Email'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <input type="email" class="ctrl-field"
            :value="getFieldValue(ctrl)" :placeholder="ctrl.props.placeholder || '请输入邮箱'"
            :readonly="readonly || ctrl.props.readonly" :disabled="ctrl.props.disabled"
            @input="onFieldInput(ctrl, $event.target.value)" />
        </div>

        <!-- DatePicker -->
        <div v-else-if="ctrl.type === 'DatePicker'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <input :type="ctrl.props.type === 'datetime' ? 'datetime-local' : 'date'" class="ctrl-field"
            :value="getFieldValue(ctrl)"
            :style="{ textAlign: ctrl.props.align || 'left' }"
            :readonly="readonly || ctrl.props.readonly" :disabled="ctrl.props.disabled"
            @input="onFieldInput(ctrl, $event.target.value)"
            @focus="executeEvent(ctrl, 'onFocus', { value: getFieldValue(ctrl), field: getFieldName(ctrl) })"
            @blur="executeEvent(ctrl, 'onBlur', { value: getFieldValue(ctrl), field: getFieldName(ctrl) })" />
        </div>

        <!-- DateRange -->
        <div v-else-if="ctrl.type === 'DateRange'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <div class="date-range-unit">
            <input type="date" class="ctrl-field dr-input"
              :placeholder="ctrl.props.startPlaceholder || '开始日期'"
              :value="getDateStart(ctrl)"
              :style="{ textAlign: ctrl.props.align || 'left' }"
              :readonly="readonly || ctrl.props.readonly" :disabled="ctrl.props.disabled"
              @input="onDateRangeStart(ctrl, $event.target.value)"
              @blur="executeEvent(ctrl, 'onBlur', { value: getDateStart(ctrl), field: getFieldName(ctrl), part: 'start' })" />
            <span class="dr-sep">至</span>
            <input type="date" class="ctrl-field dr-input"
              :placeholder="ctrl.props.endPlaceholder || '结束日期'"
              :value="getDateEnd(ctrl)"
              :style="{ textAlign: ctrl.props.align || 'left' }"
              :readonly="readonly || ctrl.props.readonly" :disabled="ctrl.props.disabled"
              @input="onDateRangeEnd(ctrl, $event.target.value)"
              @blur="executeEvent(ctrl, 'onBlur', { value: getDateEnd(ctrl), field: getFieldName(ctrl), part: 'end' })" />
            <span v-if="ctrl.props.showTimeInterval && getDateStart(ctrl) && getDateEnd(ctrl)" class="dr-interval">{{ calcDays(getDateStart(ctrl), getDateEnd(ctrl)) }}天</span>
          </div>
        </div>

        <!-- TimePicker -->
        <div v-else-if="ctrl.type === 'TimePicker'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <input type="time" class="ctrl-field"
            :value="getFieldValue(ctrl)"
            :style="{ textAlign: ctrl.props.align || 'left' }"
            :readonly="readonly || ctrl.props.readonly" :disabled="ctrl.props.disabled"
            @input="onFieldInput(ctrl, $event.target.value)"
            @focus="executeEvent(ctrl, 'onFocus', { value: getFieldValue(ctrl), field: getFieldName(ctrl) })"
            @blur="executeEvent(ctrl, 'onBlur', { value: getFieldValue(ctrl), field: getFieldName(ctrl) })" />
        </div>

        <!-- Select -->
        <div v-else-if="ctrl.type === 'Select'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <select class="ctrl-field" :multiple="ctrl.props.multiple"
            :disabled="readonly || ctrl.props.disabled"
            :value="getFieldValue(ctrl)"
            @change="onSelectChange(ctrl, $event.target.value)">
            <option v-if="ctrl.props.prompt" value="">{{ ctrl.props.prompt }}</option>
            <!-- 数据来源模式 -->
            <template v-if="ctrl.props.sourceType === 'DataSource' && selectOptionsMap[ctrl.id]">
              <option v-for="(opt, i) in selectOptionsMap[ctrl.id]" :key="i" :value="opt.value">{{ opt.label }}</option>
            </template>
            <!-- 选项模式 -->
            <option v-else v-for="(opt, i) in parseOptions(ctrl.props.options)" :key="i" :value="opt">{{ opt }}</option>
          </select>
        </div>

        <!-- TreeSelect -->
        <div v-else-if="ctrl.type === 'TreeSelect'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <div class="treeselect-container">
            <select v-if="!ctrl.props.multiple" class="ctrl-field"
              :disabled="readonly || ctrl.props.disabled"
              :value="getFieldValue(ctrl)"
              @change="onTreeSelectChange(ctrl, $event.target.value)">
              <option v-if="ctrl.props.placeholder" value="">{{ ctrl.props.placeholder }}</option>
              <template v-if="treeSelectOptionsMap[ctrl.id]">
                <option v-for="(opt, i) in treeSelectOptionsMap[ctrl.id]" :key="i" :value="opt.value">{{ opt.label }}</option>
              </template>
            </select>
            <div v-else class="multi-treeselect">
              <div class="selected-tags">
                <span v-for="(val, i) in getSelectedTreeValues(ctrl)" :key="i" class="selected-tag">
                  {{ val }}
                  <i class="fas fa-times" @click.stop="removeSelectedTreeValue(ctrl, val)"></i>
                </span>
                <span v-if="getSelectedTreeValues(ctrl).length === 0" class="placeholder">{{ ctrl.props.placeholder }}</span>
              </div>
              <select class="ctrl-field"
                :disabled="readonly || ctrl.props.disabled"
                @change="onTreeSelectMultiChange(ctrl, $event.target.value)">
                <option value="">-- 选择 --</option>
                <template v-if="treeSelectOptionsMap[ctrl.id]">
                  <option v-for="(opt, i) in treeSelectOptionsMap[ctrl.id]" :key="i" :value="opt.value">{{ opt.label }}</option>
                </template>
              </select>
            </div>
            <i class="fas fa-tree treeselect-icon"></i>
          </div>
        </div>

        <!-- Radio -->
        <div v-else-if="ctrl.type === 'Radio'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <div class="ctrl-inline" :class="ctrl.props.direction === 'vertical' ? 'vertical' : ''">
            <label v-for="(opt, i) in parseOptions(ctrl.props.options)" :key="i" class="ctrl-opt"
              @click="executeEvent(ctrl, 'onClick', { value: opt, field: getFieldName(ctrl) })">
              <input type="radio" :name="'radio_' + ctrl.id" :value="opt"
                :checked="getFieldValue(ctrl) === opt"
                :disabled="readonly || ctrl.props.disabled"
                @change="onFieldInput(ctrl, opt)"
                @focus="executeEvent(ctrl, 'onFocus', { value: opt, field: getFieldName(ctrl) })"
                @blur="executeEvent(ctrl, 'onBlur', { value: opt, field: getFieldName(ctrl) })" /> {{ opt }}
            </label>
          </div>
        </div>

        <!-- Checkbox -->
        <div v-else-if="ctrl.type === 'Checkbox'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <div class="ctrl-inline" :class="ctrl.props.direction === 'vertical' ? 'vertical' : ''">
            <template v-if="ctrl.props.detailck === 'GridCheckAll'">
              <label class="ctrl-opt"
                @click="executeEvent(ctrl, 'onClick', { field: getFieldName(ctrl) })">
                <input type="checkbox" :checked="isAllChecked(ctrl)" @change="toggleAllCheckboxes(ctrl, $event.checked)" :disabled="readonly || ctrl.props.disabled"
                  @focus="executeEvent(ctrl, 'onFocus', { field: getFieldName(ctrl) })"
                  @blur="executeEvent(ctrl, 'onBlur', { field: getFieldName(ctrl) })" /> 全选/反选              </label>
            </template>
            <label v-for="(opt, i) in parseOptions(ctrl.props.options)" :key="i" class="ctrl-opt"
              @click="executeEvent(ctrl, 'onClick', { value: opt, field: getFieldName(ctrl) })">
              <input type="checkbox" :value="opt"
                :checked="isChecked(ctrl, opt)"
                :disabled="readonly || ctrl.props.disabled"
                @change="onCheckboxChange(ctrl, opt, $event.target.checked)"
                @focus="executeEvent(ctrl, 'onFocus', { value: opt, field: getFieldName(ctrl) })"
                @blur="executeEvent(ctrl, 'onBlur', { value: opt, field: getFieldName(ctrl) })" /> {{ opt }}
            </label>
          </div>
        </div>

        <!-- Label -->
        <div v-else-if="ctrl.type === 'Label'" class="ctrl-wrap">
          <div :class="['label-text', ctrl.props.labelStyle === 'header' ? 'label-header' : '']" :style="getLabelDivStyle(ctrl)">
            {{ ctrl.props.content || '' }}
          </div>
        </div>

        <!-- HyperLink -->
        <div v-else-if="ctrl.type === 'HyperLink'" class="ctrl-wrap">
          <a :href="getHyperLinkHref(ctrl)" :target="ctrl.props.target || '_blank'" class="render-link"
            @click="executeEvent(ctrl, 'onClick', { field: getFieldName(ctrl), href: getHyperLinkHref(ctrl), value: getHyperLinkText(ctrl) })"
            @focus="executeEvent(ctrl, 'onFocus', { field: getFieldName(ctrl) })"
            @blur="executeEvent(ctrl, 'onBlur', { field: getFieldName(ctrl) })">
            <i v-if="ctrl.props.linktype === 'TaskLink'" class="fas fa-tasks"></i> {{ getHyperLinkText(ctrl) }}
          </a>
        </div>

        <!-- Divider -->
        <div v-else-if="ctrl.type === 'Divider'" class="ctrl-wrap">
          <hr class="dv" />
        </div>

        <!-- SerialNumber -->
        <div v-else-if="ctrl.type === 'SerialNumber'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">{{ ctrl.props.label }}</label>
          <input type="text" class="ctrl-field" readonly
            :value="getFieldValue(ctrl) || ''"
            :placeholder="ctrl.props.placeholder || '提交后自动生成'"
            @focus="executeEvent(ctrl, 'onFocus', { field: getFieldName(ctrl), value: getFieldValue(ctrl) })"
            @blur="executeEvent(ctrl, 'onBlur', { field: getFieldName(ctrl), value: getFieldValue(ctrl) })"
            @click="executeEvent(ctrl, 'onClick', { field: getFieldName(ctrl), value: getFieldValue(ctrl) })" />
        </div>

        <!-- Formula -->
        <div v-else-if="ctrl.type === 'Formula'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">{{ ctrl.props.label }}</label>
          <input type="text" class="ctrl-field" readonly :placeholder="ctrl.props.formula || '计算结果'" :value="getFieldValue(ctrl)" />
        </div>

        <!-- FileUpload -->
        <div v-else-if="ctrl.type === 'FileUpload'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">{{ ctrl.props.label }}</label>
          <input type="file" class="ctrl-field"
            :accept="ctrl.props.accept || ''" :multiple="ctrl.props.multiple"
            @change="onFileChange(ctrl, $event.target.files)"
            @focus="executeEvent(ctrl, 'onFocus', { field: getFieldName(ctrl) })"
            @blur="executeEvent(ctrl, 'onBlur', { field: getFieldName(ctrl) })"
            @click="executeEvent(ctrl, 'onClick', { field: getFieldName(ctrl) })" />
        </div>

        <!-- ImageUpload -->
        <div v-else-if="ctrl.type === 'ImageUpload'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">{{ ctrl.props.label }}</label>
          <input type="file" class="ctrl-field" accept="image/*"
            :multiple="ctrl.props.multiple"
            @change="onFileChange(ctrl, $event.target.files)"
            @focus="executeEvent(ctrl, 'onFocus', { field: getFieldName(ctrl) })"
            @blur="executeEvent(ctrl, 'onBlur', { field: getFieldName(ctrl) })"
            @click="executeEvent(ctrl, 'onClick', { field: getFieldName(ctrl) })" />
        </div>

        <!-- RichText -->
        <div v-else-if="ctrl.type === 'RichText'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <div class="richtext-editor" :class="{ 'is-readonly': readonly || ctrl.props.disabled }">
            <div class="rt-toolbar" v-if="!(readonly || ctrl.props.disabled)">
              <button class="rt-btn" title="粗体" @mousedown.prevent="execRT(ctrl, 'bold')"><b>B</b></button>
              <button class="rt-btn" title="斜体" @mousedown.prevent="execRT(ctrl, 'italic')"><i>I</i></button>
              <button class="rt-btn" title="下划线" @mousedown.prevent="execRT(ctrl, 'underline')"><u>U</u></button>
              <button class="rt-btn" title="删除线" @mousedown.prevent="execRT(ctrl, 'strikeThrough')"><s>S</s></button>
              <span class="rt-sep">|</span>
              <select class="rt-select" @change="execRT(ctrl, 'fontSize', $event.target.value)" title="字号">
                <option value="1">10px</option>
                <option value="2">13px</option>
                <option value="3" selected>16px</option>
                <option value="4">18px</option>
                <option value="5">24px</option>
                <option value="6">32px</option>
                <option value="7">48px</option>
              </select>
              <input type="color" class="rt-color" title="字体颜色" @change="execRT(ctrl, 'foreColor', $event.target.value)" value="#333333" />
              <input type="color" class="rt-color" title="背景颜色" @change="execRT(ctrl, 'hiliteColor', $event.target.value)" value="#ffffff" />
              <span class="rt-sep">|</span>
              <button class="rt-btn" title="有序列表" @mousedown.prevent="execRT(ctrl, 'insertOrderedList')"><i class="fas fa-list-ol"></i></button>
              <button class="rt-btn" title="无序列表" @mousedown.prevent="execRT(ctrl, 'insertUnorderedList')"><i class="fas fa-list-ul"></i></button>
              <span class="rt-sep">|</span>
              <button class="rt-btn" title="左对齐" @mousedown.prevent="execRT(ctrl, 'justifyLeft')"><i class="fas fa-align-left"></i></button>
              <button class="rt-btn" title="居中" @mousedown.prevent="execRT(ctrl, 'justifyCenter')"><i class="fas fa-align-center"></i></button>
              <button class="rt-btn" title="右对齐" @mousedown.prevent="execRT(ctrl, 'justifyRight')"><i class="fas fa-align-right"></i></button>
            </div>
            <div
              class="rt-content"
              :contenteditable="!(readonly || ctrl.props.disabled)"
              :style="{ minHeight: (ctrl.props.height || 200) + 'px' }"
              :data-ctrl-id="ctrl.id"
              @input="onRTInput(ctrl, $event)"
              @focus="executeEvent(ctrl, 'onFocus', { field: getFieldName(ctrl) })"
              @blur="executeEvent(ctrl, 'onBlur', { field: getFieldName(ctrl), value: getRTText(ctrl) })"
              v-html="getFieldValue(ctrl)"
            ></div>
            <div v-if="ctrl.props.maxLength" class="rt-char-count" :class="{ 'rt-over-limit': getRTCharCount(ctrl) > ctrl.props.maxLength }">
              {{ getRTCharCount(ctrl) }} / {{ ctrl.props.maxLength }}
            </div>
          </div>
        </div>

        <!-- UserPicker -->
        <div v-else-if="ctrl.type === 'UserPicker'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <div class="picker-wrap">
            <input type="text" class="ctrl-field picker-input" readonly
              :value="getFieldValue(ctrl)" :placeholder="ctrl.props.placeholder || '请选择人员'" />
            <button type="button" class="picker-btn" :disabled="readonly || ctrl.props.disabled" @click="openUserPicker(ctrl)">
              <i class="fas fa-user-plus"></i>
            </button>
          </div>
        </div>

        <!-- DeptPicker -->
        <div v-else-if="ctrl.type === 'DeptPicker'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <div class="picker-wrap">
            <input type="text" class="ctrl-field picker-input" readonly
              :value="getFieldValue(ctrl)" :placeholder="ctrl.props.placeholder || '请选择部门'" />
            <button type="button" class="picker-btn" :disabled="readonly || ctrl.props.disabled" @click="openDeptPicker(ctrl)">
              <i class="fas fa-building"></i>
            </button>
          </div>
        </div>

        <!-- Position -->
        <div v-else-if="ctrl.type === 'Position'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">
            <span v-if="ctrl.props.required" class="req">*</span>{{ ctrl.props.label }}
          </label>
          <div class="picker-wrap">
            <input type="text" class="ctrl-field picker-input" readonly
              :value="getFieldValue(ctrl)" :placeholder="ctrl.props.placeholder || '请选择岗位'" />
            <button type="button" class="picker-btn" :disabled="readonly || ctrl.props.disabled" @click="openPositionPicker(ctrl)">
              <i class="fas fa-briefcase"></i>
            </button>
          </div>
        </div>

        <!-- Tabs -->
        <div v-else-if="ctrl.type === 'Tabs'" class="ctrl-wrap tabs-wrap">
          <div class="tabs-bar">
            <div v-for="(tab, ti) in (ctrl.props.tabs || [])" :key="ti"
              class="tab-tag" :class="{ active: activeTabMap[ctrl.id] === ti }"
              @click="activeTabMap[ctrl.id] = ti">{{ tab.title }}</div>
          </div>
          <div class="tabs-content">
            <template v-if="ctrl.props.tabs && ctrl.props.tabs[activeTabMap[ctrl.id] || 0]">
              <template v-for="child in (ctrl.props.tabs[activeTabMap[ctrl.id] || 0].children || [])" :key="child.id">
                <RenderControl :ctrl="child" :formData="formData" :readonly="readonly"
                  @update="onChildUpdate" @picker="onPickerEvent" />
              </template>
            </template>
          </div>
        </div>

        <!-- SubTable -->
        <div v-else-if="ctrl.type === 'SubTable'" class="ctrl-wrap sub-table-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">{{ ctrl.props.label }}</label>
          <table class="sub-table" :align="ctrl.props.align || 'left'">
            <thead>
              <tr v-for="(hRow, hi) in getSubHeaderRows(ctrl)" :key="'hr'+hi">
                <th v-for="(cell, ci) in hRow" :key="'hc'+ci"
                  :colspan="cell.colspan" :rowspan="cell.rowspan"
                  :style="getSubColWidthStyle(cell.columns[0])">
                  {{ cell.text }}
                  <span v-if="cell.columns && cell.columns.length === 1 && cell.columns[0].props?.required" class="req">*</span>
                </th>
                <th v-if="hasOperationColumn(ctrl) && hi === 0" :rowspan="getSubHeaderRows(ctrl).length" style="width:80px">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, ri) in getSubTableRows(ctrl)" :key="ri">
                <td v-for="col in (ctrl.props.columns || []).filter(c => c.type !== '_operation')" :key="col.id" :style="getSubColWidthStyle(col)">
                  <SubTableCell :col="col" :row-data="row" :row-index="ri"
                    :readonly="readonly || col.props?.readonly || col.props?.disabled"
                    @update="onSubTableCellUpdate(ctrl, ri, col, $event)" />
                </td>
                <td v-if="hasOperationColumn(ctrl)" style="text-align:center;white-space:nowrap;">
                  <button type="button" class="row-add-btn-inline" :disabled="readonly" @click="addSubTableRow(ctrl)"
                    v-if="getOperationCol(ctrl)?.props?.operationType?.includes('add')">
                    <i class="fas fa-plus"></i>
                  </button>
                  <button type="button" class="row-del-btn" :disabled="readonly"
                    @click="removeSubTableRow(ctrl, ri)"
                    v-if="getOperationCol(ctrl)?.props?.operationType?.includes('delete') && getSubTableRows(ctrl).length > (ctrl.props.initRows || 1)">
                    <i class="fas fa-times"></i>
                  </button>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="getHasFooter(ctrl)">
              <tr>
                <td v-for="col in (ctrl.props.columns || []).filter(c => c.type !== '_operation')" :key="'f'+col.id" :style="getSubColWidthStyle(col)" class="sub-footer-cell">
                  <span v-if="col.props?.footerType === 'sum'" class="footer-text">合计：{{ computeSubColSum(ctrl, col) }}</span>
                  <span v-else-if="col.props?.footerType === 'avg'" class="footer-text">平均：{{ computeSubColAvg(ctrl, col) }}</span>
                  <span v-else-if="col.props?.footerType === 'count'" class="footer-text">计数：{{ getSubTableRows(ctrl).length }}</span>
                  <span v-else-if="col.props?.footerType === 'text'" class="footer-text">{{ col.props?.footerText || '' }}</span>
                  <button v-else-if="col.props?.footerType === 'button'" class="footer-btn" :class="'btn-'+(col.props?.buttontype||'default')"
                    :disabled="readonly" @click="onFooterButtonClick(ctrl, col)">
                    {{ col.props?.footerText || col.props?.text || '按钮' }}
                  </button>
                </td>
                <td v-if="hasOperationColumn(ctrl)"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- TableLayout -->
        <div v-else-if="ctrl.type === 'TableLayout'" class="ctrl-wrap">
          <label class="ctrl-label" v-if="ctrl.props.label" :style="getCtrlLabelStyle(ctrl)">{{ ctrl.props.label }}</label>
          <table class="layout-table">
            <tr v-for="r in (ctrl.props.rows || 1)" :key="r">
              <td v-for="c in (ctrl.props.cols || 1)" :key="c" class="layout-cell">
                <template v-for="child in (ctrl.children || [])" :key="child.id">
                  <RenderControl :ctrl="child" :formData="formData" :readonly="readonly"
                    @update="onChildUpdate" @picker="onPickerEvent" />
                </template>
              </td>
            </tr>
          </table>
        </div>

        <!-- Button -->
        <div v-else-if="ctrl.type === 'Button'" class="ctrl-wrap">
          <button type="button" class="btn-render"
            :class="'btn-' + (ctrl.props.type || 'default') + ' sz-' + (ctrl.props.size || 'medium')"
            :disabled="readonly"
            @click="onButtonClick(ctrl)">
            {{ getButtonLabel(ctrl) }}
          </button>
        </div>

        <!-- Fallback -->
        <div v-else class="ctrl-wrap">
          <input type="text" class="ctrl-field" :value="getFieldValue(ctrl)"
            :placeholder="ctrl.props?.placeholder || ''"
            @input="onFieldInput(ctrl, $event.target.value)" />
        </div>
      </div>
    </div>

    <!-- Lookup弹窗 -->
    <div v-if="lookupDialog.visible" class="modal-overlay" @click.self="lookupDialog.visible = false">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3>{{ lookupDialog.title || '选择数据' }}</h3>
          <button class="modal-close" @click="lookupDialog.visible = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="lookup-search" v-if="lookupDialog.showSearch">
            <input type="text" v-model="lookupDialog.searchText" placeholder="输入关键字搜索..." class="search-input" @keyup.enter="searchLookupData" />
            <button class="search-btn" @click="searchLookupData"><i class="fas fa-search"></i></button>
          </div>
          <div class="lookup-table-wrap">
            <table class="lookup-table">
              <thead>
                <tr>
                  <th v-if="lookupDialog.multiSelect" class="col-check"><input type="checkbox" @change="toggleAllLookupRows($event)" /></th>
                  <th v-for="col in lookupDialog.columns" :key="col.field">{{ col.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in lookupDialog.data" :key="idx" :class="{ selected: isSelectedLookupRow(row) }" @click="selectLookupRow(row)">
                  <td v-if="lookupDialog.multiSelect" class="col-check"><input type="checkbox" :checked="isSelectedLookupRow(row)" @click.stop="toggleLookupRow(row, $event)" /></td>
                  <td v-for="col in lookupDialog.columns" :key="col.field">{{ row[col.field] }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="lookupDialog.data.length === 0" class="empty-data">暂无数据</div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-default" @click="lookupDialog.visible = false">取消</button>
          <button class="btn btn-primary" @click="confirmLookup">确定</button>
        </div>
      </div>
    </div>

    <!-- 人员选择器弹窗-->
    <div v-if="userPickerDialog.visible" class="modal-overlay" @click.self="userPickerDialog.visible = false">
      <div class="modal-dialog modal-small">
        <div class="modal-header">
          <h3>选择人员</h3>
          <button class="modal-close" @click="userPickerDialog.visible = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="picker-search">
            <input type="text" v-model="userPickerDialog.searchText" placeholder="输入姓名搜索..." class="search-input" @keyup.enter="searchUsers" />
            <button class="search-btn" @click="searchUsers"><i class="fas fa-search"></i></button>
          </div>
          <div class="picker-list">
            <div v-for="user in userPickerDialog.users" :key="user.id" class="picker-item" :class="{ selected: isSelectedUser(user) }" @click="toggleUser(user)">
              <input type="checkbox" :checked="isSelectedUser(user)" @click.stop />
              <span class="user-name">{{ user.name }}</span>
              <span class="user-dept">{{ user.department }}</span>
            </div>
            <div v-if="userPickerDialog.users.length === 0" class="empty-data">暂无用户数据</div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-default" @click="userPickerDialog.visible = false">取消</button>
          <button class="btn btn-primary" @click="confirmUserPicker">确定</button>
        </div>
      </div>
    </div>

    <!-- 部门选择器弹窗-->
    <div v-if="deptPickerDialog.visible" class="modal-overlay" @click.self="deptPickerDialog.visible = false">
      <div class="modal-dialog modal-small">
        <div class="modal-header">
          <h3>选择部门</h3>
          <button class="modal-close" @click="deptPickerDialog.visible = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="picker-list">
            <div v-for="dept in deptPickerDialog.depts" :key="dept.id" class="picker-item" :class="{ selected: isSelectedDept(dept) }" @click="toggleDept(dept)">
              <input type="checkbox" :checked="isSelectedDept(dept)" @click.stop />
              <span class="dept-name">{{ dept.name }}</span>
              <span class="dept-code">{{ dept.code }}</span>
            </div>
            <div v-if="deptPickerDialog.depts.length === 0" class="empty-data">暂无部门数据</div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-default" @click="deptPickerDialog.visible = false">取消</button>
          <button class="btn btn-primary" @click="confirmDeptPicker">确定</button>
        </div>
      </div>
    </div>

    <!-- 岗位选择器弹窗-->
    <div v-if="positionPickerDialog.visible" class="modal-overlay" @click.self="positionPickerDialog.visible = false">
      <div class="modal-dialog modal-small">
        <div class="modal-header">
          <h3>选择岗位</h3>
          <button class="modal-close" @click="positionPickerDialog.visible = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="picker-list">
            <div v-for="pos in positionPickerDialog.positions" :key="pos.id" class="picker-item" :class="{ selected: isSelectedPosition(pos) }" @click="togglePosition(pos)">
              <input type="checkbox" :checked="isSelectedPosition(pos)" @click.stop />
              <span class="pos-name">{{ pos.name }}</span>
              <span class="pos-dept">{{ pos.department }}</span>
            </div>
            <div v-if="positionPickerDialog.positions.length === 0" class="empty-data">暂无岗位数据</div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-default" @click="positionPickerDialog.visible = false">取消</button>
          <button class="btn btn-primary" @click="confirmPositionPicker">确定</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, h, defineComponent, onMounted } from 'vue'
import { executeSql } from '../api/database'
import { getUsers, getDepartments, getPositions } from '../api/user'
import { getSelectOptions } from '../api/process'

// ============ Select Options Cache ============
const selectOptionsMap = reactive({})

// ============ TreeSelect Options Cache ============
const treeSelectOptionsMap = reactive({})
// 存储原始树形数据，用于数据映射
const treeSelectRawDataMap = reactive({})

const props = defineProps({
  controls: { type: Array, default: () => [] },
  formData: { type: Object, default: () => ({}) },
  readonly: { type: Boolean, default: false }
})

const emit = defineEmits(['update'])

// Tab active state per Tabs control
const activeTabMap = reactive({})

// SubTable rows store: ctrlId -> [{}, {}, ...]
const subTableRowsMap = reactive({})

// ============ Lookup Dialog State ============
const lookupDialog = reactive({
  visible: false,
  title: '',
  sourceControl: null,
  showSearch: true,
  multiSelect: false,
  searchText: '',
  columns: [],
  data: [],
  selectedRows: [],
  // config from button props
  browseTable: '',
  browseShowFields: '',
  browseFilter: '',
  browseOrderby: '',
  browseDataSource: '',
  browseCustomUrl: '',
  dataMap: ''
})

// ============ User Picker Dialog State ============
const userPickerDialog = reactive({
  visible: false,
  searchText: '',
  users: [],
  selectedUsers: [],
  sourceControl: null,
  multiple: false
})

// ============ Dept Picker Dialog State ============
const deptPickerDialog = reactive({
  visible: false,
  depts: [],
  selectedDepts: [],
  sourceControl: null,
  multiple: false
})

// ============ Position Picker Dialog State ============
const positionPickerDialog = reactive({
  visible: false,
  positions: [],
  selectedPositions: [],
  sourceControl: null,
  multiple: false
})

// Get label style (color + bold, required fields default to red+bold)
const getCtrlLabelStyle = (ctrl) => {
  const p = ctrl.props || {}
  const style = {}
  if (p.labelColor) {
    style.color = p.labelColor
  } else if (p.required) {
    style.color = '#ef4444'
  }
  if (p.labelBold !== undefined) {
    style.fontWeight = p.labelBold ? 'bold' : 'normal'
  } else if (p.required) {
    style.fontWeight = 'bold'
  }
  return Object.keys(style).length > 0 ? style : null
}

// Get label div style (for Label control - background, border radius, padding, etc.)
const getLabelDivStyle = (ctrl) => {
  const p = ctrl.props || {}
  const isHeader = p.labelStyle === 'header'
  const style = {}

  if (isHeader) {
    if (p.color) style.color = p.color
    if (p.fontSize) style.fontSize = p.fontSize + 'px'
    if (p.align) style.textAlign = p.align
  } else {
    style.fontSize = (p.fontSize || 13) + 'px'
    style.textAlign = p.align || 'left'
    style.color = p.color || '#333'
  }

  if (p.backgroundColor) style.backgroundColor = p.backgroundColor
  if (p.borderRadius !== undefined && p.borderRadius !== '') style.borderRadius = p.borderRadius + 'px'
  if (p.padding) style.padding = p.padding + 'px'
  if (p.bold) style.fontWeight = 'bold'
  if (p.lineHeight) style.lineHeight = p.lineHeight + 'px'
  if (p.boxShadow) style.boxShadow = p.boxShadow
  if (p.letterSpacing) style.letterSpacing = p.letterSpacing + 'px'
  return style
}

// Initialize subtable rows
const initSubTableRows = () => {
  for (const ctrl of props.controls || []) {
    if (ctrl.type === 'SubTable') {
      const initData = getSubTableInitData(ctrl)
      subTableRowsMap[ctrl.id] = initData.length > 0 ? JSON.parse(JSON.stringify(initData)) : createEmptyRows(ctrl)
    }
  }
}

// Get initial data from formData for subtable
const getSubTableInitData = (ctrl) => {
  const tableName = (ctrl.props?.formbindtable || '').toLowerCase()
  // Try nested format { detail: { tableName: [...] } }
  if (props.formData?.detail && props.formData.detail[tableName]) {
    return props.formData.detail[tableName]
  }
  // Try flat format
  if (Array.isArray(props.formData[tableName])) {
    return props.formData[tableName]
  }
  return []
}

// Create empty rows based on initRows
const createEmptyRows = (ctrl) => {
  const count = ctrl.props?.initRows || 1
  const columns = (ctrl.props?.columns || []).filter(c => c.type !== '_operation')
  const rows = []
  for (let i = 0; i < count; i++) {
    const row = {}
    for (const col of columns) {
      const fieldName = col.props?.dataSourceField || col.props?.fieldName || col.props?.bindColumn || col.id
      row[fieldName] = col.props?.defaultValue || ''
    }
    rows.push(row)
  }
  return rows
}

// Get subtable rows
const getSubTableRows = (ctrl) => {
  if (!subTableRowsMap[ctrl.id]) {
    const initData = getSubTableInitData(ctrl)
    subTableRowsMap[ctrl.id] = initData.length > 0 ? JSON.parse(JSON.stringify(initData)) : createEmptyRows(ctrl)
  }
  return subTableRowsMap[ctrl.id]
}

// Add subtable row
const addSubTableRow = (ctrl) => {
  if (!subTableRowsMap[ctrl.id]) {
    subTableRowsMap[ctrl.id] = createEmptyRows(ctrl)
  }
  const columns = (ctrl.props?.columns || []).filter(c => c.type !== '_operation')
  const newRow = {}
  for (const col of columns) {
    const fieldName = col.props?.dataSourceField || col.props?.fieldName || col.props?.bindColumn || col.id
    newRow[fieldName] = col.props?.defaultValue || ''
  }
  subTableRowsMap[ctrl.id].push(newRow)
  emitFormUpdate()
}

// Remove subtable row
const removeSubTableRow = (ctrl, index) => {
  if (subTableRowsMap[ctrl.id]) {
    subTableRowsMap[ctrl.id].splice(index, 1)
    emitFormUpdate()
  }
}

// SubTable operation column helpers
const hasOperationColumn = (ctrl) => (ctrl.props?.columns || []).some(c => c.type === '_operation')
const getOperationCol = (ctrl) => (ctrl.props?.columns || []).find(c => c.type === '_operation') || null

// HyperLink helpers
const getHyperLinkText = (ctrl) => {
  // If data source field is set, get value from data source (with default value priority)
  if (ctrl.props?.dataSourceField) {
    const val = getFieldValue(ctrl)
    if (val !== '' && val !== undefined && val !== null) return String(val)
  }
  // Fallback to content property (direct text)
  return ctrl.props?.content || '链接'
}

const getHyperLinkHref = (ctrl) => {
  return ctrl.props?.href || '#'
}

// --- RichText helpers ---
const execRT = (ctrl, command, value) => {
  document.execCommand(command, false, value || null)
  // Update formData after formatting
  updateRTValue(ctrl)
}

const onRTInput = (ctrl, event) => {
  const div = event.target
  const html = div.innerHTML
  const plainText = getRTTextFromHTML(html)

  // Chinese character length validation
  if (ctrl.props?.maxLength) {
    const charLen = getCharLength(plainText)
    if (charLen > ctrl.props.maxLength) {
      // Truncate content to fit maxLength
      let truncated = ''
      let currentLen = 0
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      // Try to truncate by text content
      for (const node of tempDiv.childNodes) {
        const nodeText = node.textContent || ''
        let added = ''
        for (const ch of nodeText) {
          if (currentLen >= ctrl.props.maxLength) break
          added += ch
          currentLen += 1
        }
        if (added && node.nodeType === 3) {
          truncated += added
        } else if (added && node.nodeType === 1) {
          const clone = node.cloneNode(true)
          clone.textContent = added
          truncated += clone.outerHTML
        }
        if (currentLen >= ctrl.props.maxLength) break
      }
      if (!truncated) truncated = ''
      // Restore cursor position workaround: just set truncated HTML
      updateRTValueDirect(ctrl, truncated)
      // Try to restore to end
      const el = document.querySelector(`[data-ctrl-id="${ctrl.id}"]`)
      if (el) {
        el.innerHTML = truncated
        const range = document.createRange()
        range.selectNodeContents(el)
        range.collapse(false)
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(range)
      }
      return
    }
  }

  updateRTValueDirect(ctrl, html)
}

const updateRTValue = (ctrl) => {
  const el = document.querySelector(`[data-ctrl-id="${ctrl.id}"]`)
  if (el) {
    updateRTValueDirect(ctrl, el.innerHTML)
  }
}

const updateRTValueDirect = (ctrl, html) => {
  const fieldName = getFieldName(ctrl)
  const tableName = ctrl.props?.dataTargetTable || ctrl.props?.dataSourceTable || ''
  if (tableName) {
    emit('update', { type: 'main', table: tableName, field: fieldName, value: html })
  } else {
    emit('update', { [fieldName]: html })
  }
  // Trigger onChange event
  executeEvent(ctrl, 'onChange', { value: html, field: fieldName })
}

const getRTText = (ctrl) => {
  const el = document.querySelector(`[data-ctrl-id="${ctrl.id}"]`)
  return el ? getRTTextFromHTML(el.innerHTML) : ''
}

const getRTCharCount = (ctrl) => {
  const el = document.querySelector(`[data-ctrl-id="${ctrl.id}"]`)
  if (!el) return 0
  return getCharLength(getRTTextFromHTML(el.innerHTML))
}

const getRTTextFromHTML = (html) => {
  if (!html) return ''
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  return tempDiv.textContent || tempDiv.innerText || ''
}
// --- end RichText helpers ---

const getFieldName = (ctrl) => {
  return ctrl.props?.dataSourceField || ctrl.props?.fieldName || ctrl.props?.bindColumn || ctrl.props?.name || ctrl.id
}

const getFieldValue = (ctrl) => {
  const fieldName = getFieldName(ctrl)
  // Default value takes priority over data source, with system variable resolution
  if (ctrl.props?.defaultValue) {
    return resolveSysVar(String(ctrl.props.defaultValue))
  }
  // Try nested format
  const tableName = ctrl.props?.dataTargetTable || ctrl.props?.dataSourceTable || ''
  if (tableName && props.formData?.main && props.formData.main[tableName]) {
    return props.formData.main[tableName][fieldName] ?? ''
  }
  return props.formData[fieldName] ?? ''
}

// Resolve ${variable} system variable placeholders in default values
function resolveSysVar(value) {
  if (!value || typeof value !== 'string') return value
  if (!value.includes('${')) return value
  return value.replace(/\$\{([^}]+)\}/g, (match, varName) => {
    switch (varName) {
      case 'syscurrentdatetime': {
        const n = new Date()
        const pad = v => String(v).padStart(2, '0')
        return `${n.getFullYear()}-${pad(n.getMonth()+1)}-${pad(n.getDate())} ${pad(n.getHours())}:${pad(n.getMinutes())}:${pad(n.getSeconds())}`
      }
      case 'syscurrenttime': {
        const n = new Date()
        const pad = v => String(v).padStart(2, '0')
        return `${pad(n.getHours())}:${pad(n.getMinutes())}:${pad(n.getSeconds())}`
      }
      case 'systoday': {
        const n = new Date()
        const pad = v => String(v).padStart(2, '0')
        return `${n.getFullYear()}-${pad(n.getMonth()+1)}-${pad(n.getDate())}`
      }
      case 'systaskid':
        return String(props.formData?.taskId || props.formData?.task?.taskId || '')
      case 'sysloginuser.account':
        return String(props.formData?.currentUser?.account || '')
      case 'sysloginuser.displayname':
        return String(props.formData?.currentUser?.displayName || '')
      case 'sysloginuser.email':
        return String(props.formData?.currentUser?.email || '')
      case 'sysloginuser.tel':
        return String(props.formData?.currentUser?.tel || '')
      case 'sysloginuser.mb':
        return String(props.formData?.currentUser?.mb || '')
      case 'sysloginuser.hrid':
        return String(props.formData?.currentUser?.hrId || '')
      case 'sysloginuser.job':
        return String(props.formData?.currentUser?.job || '')
      case 'sysloginuser.office':
        return String(props.formData?.currentUser?.office || '')
      case 'sysloginuser.costcenter':
        return String(props.formData?.currentUser?.costCenter || '')
      case 'sysloginuser.levels':
        return String(props.formData?.currentUser?.levels || '')
      case 'sysloginuser.description':
        return String(props.formData?.currentUser?.description || '')
      case 'sysloginuser.birthday':
        return String(props.formData?.currentUser?.birthday || '')
      case 'sysloginuser.joindate':
        return String(props.formData?.currentUser?.joinDate || '')
      case 'sysloginuser.qq':
        return String(props.formData?.currentUser?.qq || '')
      default:
        return match
    }
  })
}

const onFieldInput = (ctrl, value) => {
  const fieldName = getFieldName(ctrl)
  // Chinese character length validation for Input/Textarea type
  if ((ctrl.type === 'Input' || ctrl.type === 'Textarea') && ctrl.props?.maxLength && value) {
    const charLen = getCharLength(value)
    if (charLen > ctrl.props.maxLength) {
      // Truncate to max length (considering Chinese chars as 1)
      let truncated = ''
      let currentLen = 0
      for (const ch of value) {
        const cl = ch.charCodeAt(0) > 127 ? 1 : 1
        if (currentLen + cl > ctrl.props.maxLength) break
        truncated += ch
        currentLen += cl
      }
      value = truncated
    }
  }
  const tableName = ctrl.props?.dataTargetTable || ctrl.props?.dataSourceTable || ''
  if (tableName) {
    emit('update', { type: 'main', table: tableName, field: fieldName, value })
  } else {
    emit('update', { [fieldName]: value })
  }
  // Execute onChange event
  executeEvent(ctrl, 'onChange', { value, field: fieldName })
}

// Select change: value + datamap
const onSelectChange = async (ctrl, value) => {
  const fieldName = getFieldName(ctrl)
  const tableName = ctrl.props?.dataTargetTable || ctrl.props?.dataSourceTable || ''
  if (tableName) {
    emit('update', { type: 'main', table: tableName, field: fieldName, value })
  } else {
    emit('update', { [fieldName]: value })
  }

  // 数据映射：选中后回写其他字段
  await applyDataMap(ctrl, value)

  // Execute onChange event
  executeEvent(ctrl, 'onChange', { value, field: fieldName })
}

// Apply datamap: 绑定表选中后回写目标字段
const applyDataMap = async (ctrl, value) => {
  const datamap = ctrl.props?.datamap
  if (!datamap || !value) return

  const table = ctrl.props?.selectbindtable
  const valueCol = ctrl.props?.selectvaluecolumn
  if (!table || !valueCol) return

  // 解析过滤条件中的占位符（子表行用 #name, 主表用 @Id）
  let filter = ctrl.props?.selectfilter || ''
  if (filter.includes('@Id')) {
    filter = filter.replace(/@Id/gi, (props.formData?.main?.Id || props.formData?.Id || '') + '')
  }
  // #name 暂不处理（子表行级）

  try {
    const params = {
      tableName: table,
      valueColumn: valueCol,
      displayColumn: ctrl.props?.selectdisplaycolumn || valueCol,
      filter: filter,
      orderBy: ctrl.props?.browseorderby || ''
    }
    const res = await getSelectOptions(params)
    const list = res.data || res.Data || res || []
    const row = list.find(r => {
      const v = r[valueCol] ?? r[valueCol.toLowerCase()] ?? ''
      return String(v) === String(value)
    })
    if (!row) return

    // 解析 datamap：source=target,source2=target2
    const mappings = datamap.split(',').map(s => s.trim()).filter(Boolean)
    for (const mapping of mappings) {
      const eq = mapping.indexOf('=')
      if (eq < 0) continue
      const sourceField = mapping.substring(0, eq).trim()
      const targetField = mapping.substring(eq + 1).trim()
      if (!sourceField || !targetField) continue

      const srcValue = row[sourceField] ?? row[sourceField.toLowerCase()] ?? ''
      const fn = targetField
      const tn = ctrl.props?.dataTargetTable || ctrl.props?.dataSourceTable || ''
      if (tn) {
        emit('update', { type: 'main', table: tn, field: fn, value: String(srcValue) })
      } else {
        emit('update', { [fn]: String(srcValue) })
      }
    }
  } catch (e) {
    console.warn('数据映射失败:', e)
  }
}

// Load dynamic select options from database
const loadSelectOptions = async (ctrl) => {
  if (ctrl.props?.sourceType !== 'DataSource') return
  const table = ctrl.props?.selectbindtable
  const valueCol = ctrl.props?.selectvaluecolumn
  const displayCol = ctrl.props?.selectdisplaycolumn || valueCol
  if (!table || !valueCol) return

  // 处理过滤条件中的占位符
  let filter = ctrl.props?.selectfilter || ''
  if (filter.includes('@Id')) {
    const idVal = props.formData?.main?.Id || props.formData?.Id || ''
    filter = filter.replace(/@Id/gi, String(idVal))
  }
  // #name 占位符暂不替换（子表行级）

  try {
    const res = await getSelectOptions({
      tableName: table,
      valueColumn: valueCol,
      displayColumn: displayCol,
      filter: filter,
      orderBy: ctrl.props?.browseorderby || ''
    })
    const list = res.data || res.Data || res || []
    selectOptionsMap[ctrl.id] = list.map(row => ({
      value: String(row[valueCol] ?? ''),
      label: String(row[displayCol] ?? row[displayCol.toLowerCase()] ?? row[valueCol] ?? '')
    }))
  } catch (e) {
    console.warn('加载选项失败:', e)
    selectOptionsMap[ctrl.id] = []
  }
}

// ============ TreeSelect Options Loading ============
const loadTreeSelectOptions = async (ctrl) => {
  const table = ctrl.props?.selectbindtable
  const valueCol = ctrl.props?.selectvaluecolumn
  const displayCol = ctrl.props?.selectdisplaycolumn || valueCol
  if (!table || !valueCol) return

  let filter = ctrl.props?.selectfilter || ''
  if (filter.includes('@Id')) {
    const idVal = props.formData?.main?.Id || props.formData?.Id || ''
    filter = filter.replace(/@Id/gi, String(idVal))
  }

  try {
    const res = await getSelectOptions({
      tableName: table,
      valueColumn: valueCol,
      displayColumn: displayCol,
      filter: filter,
      orderBy: ctrl.props?.browseorderby || ''
    })
    const list = res.data || res.Data || res || []
    
    // 构建选项列表
    const options = list.map(row => ({
      value: String(row[valueCol] ?? ''),
      label: String(row[displayCol] ?? row[displayCol.toLowerCase()] ?? row[valueCol] ?? '')
    }))
    
    // 处理树形结构
    const treeIdField = ctrl.props?.treeIdField || valueCol
    const treeParentIdField = ctrl.props?.treeParentIdField
    const treeRootValue = ctrl.props?.treeRootValue
    
    if (treeParentIdField && treeIdField) {
      // 构建树形结构
      const treeData = []
      const nodeMap = new Map()
      
      // 创建所有节点
      list.forEach(row => {
        const id = String(row[treeIdField] ?? '')
        const label = String(row[displayCol] ?? row[displayCol.toLowerCase()] ?? row[valueCol] ?? '')
        nodeMap.set(id, {
          value: String(row[valueCol] ?? ''),
          label: label,
          children: []
        })
      })
      
      // 建立父子关系
      list.forEach(row => {
        const id = String(row[treeIdField] ?? '')
        const parentId = String(row[treeParentIdField] ?? treeRootValue ?? '')
        const node = nodeMap.get(id)
        
        if (parentId === treeRootValue || parentId === '' || !nodeMap.has(parentId)) {
          // 根节点
          treeData.push(node)
        } else {
          // 子节点
          const parent = nodeMap.get(parentId)
          if (parent) {
            parent.children.push(node)
          }
        }
      })
      
      // 递归遍历树，生成扁平列表（带缩进标记）
      function flattenTree(nodes, level = 0) {
        const result = []
        nodes.forEach(node => {
          const indent = '  '.repeat(level)
          result.push({
            value: node.value,
            label: indent + node.label
          })
          if (node.children && node.children.length > 0) {
            result.push(...flattenTree(node.children, level + 1))
          }
        })
        return result
      }
      
      treeSelectOptionsMap[ctrl.id] = flattenTree(treeData)
    } else {
      // 非树形结构，直接使用
      treeSelectOptionsMap[ctrl.id] = options
    }
    // 存储原始数据，供数据映射使用
    treeSelectRawDataMap[ctrl.id] = list
  } catch (e) {
    console.warn('加载TreeSelect选项失败:', e)
    treeSelectOptionsMap[ctrl.id] = []
  }
}

// TreeSelect 单选事件
const onTreeSelectChange = (ctrl, value) => {
  const fieldName = getFieldName(ctrl)
  if (!fieldName) return
  
  // 设置值
  const currentData = { ...(props.formData.main || {}) }
  currentData[fieldName] = value
  
  emit('update', {
    main: currentData,
    sub: props.formData.sub
  })
  
  // 执行数据映射（使用缓存的原始数据）
  const rawData = treeSelectRawDataMap[ctrl.id] || []
  applyDataMapping(ctrl, value, rawData)
  
  // 触发 onChange 事件
  executeEvent(ctrl, 'onChange', { value: value, field: fieldName })
}

// TreeSelect 多选事件
const onTreeSelectMultiChange = (ctrl, value) => {
  if (!value) return
  
  const fieldName = getFieldName(ctrl)
  if (!fieldName) return
  
  // 获取当前值
  const currentVal = getFieldValue(ctrl)
  let values = Array.isArray(currentVal) ? [...currentVal] : (currentVal ? String(currentVal).split(',') : [])
  
  // 添加新值（避免重复）
  if (!values.includes(value)) {
    values.push(value)
  }
  
  // 设置值（逗号分隔的字符串）
  const valStr = values.join(',')
  const currentData = { ...(props.formData.main || {}) }
  currentData[fieldName] = valStr
  
  emit('update', {
    main: currentData,
    sub: props.formData.sub
  })
  
  // 触发 onChange 事件
  executeEvent(ctrl, 'onChange', { value: valStr, field: fieldName })
}

// 获取 TreeSelect 多选已选项的显示值
const getSelectedTreeValues = (ctrl) => {
  const val = getFieldValue(ctrl)
  if (!val) return []
  
  const options = treeSelectOptionsMap[ctrl.id] || []
  const values = Array.isArray(val) ? val : String(val).split(',').filter(Boolean)
  
  return values.map(v => {
    const opt = options.find(o => o.value === v)
    return opt ? opt.label : v
  })
}

// 移除 TreeSelect 多选中的某个值
const removeSelectedTreeValue = (ctrl, val) => {
  const fieldName = getFieldName(ctrl)
  if (!fieldName) return
  
  const currentVal = getFieldValue(ctrl)
  let values = Array.isArray(currentVal) ? [...currentVal] : String(currentVal).split(',').filter(Boolean)
  values = values.filter(v => v !== val)
  
  const valStr = values.join(',')
  const currentData = { ...(props.formData.main || {}) }
  currentData[fieldName] = valStr
  
  emit('update', {
    main: currentData,
    sub: props.formData.sub
  })
  
  executeEvent(ctrl, 'onChange', { value: valStr, field: fieldName })
}

// 应用数据映射
const applyDataMapping = (ctrl, value, list) => {
  const dataMapList = ctrl.props?.datamapList
  if (!dataMapList || !Array.isArray(dataMapList) || dataMapList.length === 0) return
  
  // 找到选中行的数据
  const valueCol = ctrl.props?.selectvaluecolumn
  const selectedRow = list.find(row => String(row[valueCol]) === String(value))
  if (!selectedRow) return
  
  // 应用映射
  const currentData = { ...(props.formData.main || {}) }
  dataMapList.forEach(mapping => {
    if (mapping.sourceField && mapping.targetTable && mapping.targetField) {
      const sourceValue = selectedRow[mapping.sourceField]
      currentData[mapping.targetField] = sourceValue
    }
  })
  
  emit('update', {
    main: currentData,
    sub: props.formData.sub
  })
}

// Checkbox helpers
const isChecked = (ctrl, opt) => {
  const val = getFieldValue(ctrl)
  if (!val) return false
  if (Array.isArray(val)) return val.includes(opt)
  return String(val).split(',').includes(opt)
}

const isAllChecked = (ctrl) => {
  const opts = parseOptions(ctrl.props?.options)
  return opts.length > 0 && opts.every(opt => isChecked(ctrl, opt))
}

const toggleAllCheckboxes = (ctrl, checked) => {
  const opts = parseOptions(ctrl.props?.options)
  const val = checked ? opts.join(',') : ''
  onFieldInput(ctrl, val)
}

const onCheckboxChange = (ctrl, opt, checked) => {
  const val = getFieldValue(ctrl)
  let arr = val ? String(val).split(',').filter(Boolean) : []
  if (checked) {
    if (!arr.includes(opt)) arr.push(opt)
  } else {
    arr = arr.filter(v => v !== opt)
  }
  onFieldInput(ctrl, arr.join(','))
}

// DateRange helpers
const getDateStart = (ctrl) => {
  const val = getFieldValue(ctrl)
  if (!val) return ''
  if (typeof val === 'object' && val.start) return val.start
  return String(val).split('~')[0] || ''
}

const getDateEnd = (ctrl) => {
  const val = getFieldValue(ctrl)
  if (!val) return ''
  if (typeof val === 'object' && val.end) return val.end
  return String(val).split('~')[1] || ''
}

const onDateRangeStart = (ctrl, val) => {
  const end = getDateEnd(ctrl)
  const value = end ? `${val}~${end}` : val
  onFieldInput(ctrl, value)
  executeEvent(ctrl, 'onChange', { value, field: getFieldName(ctrl) })
}

const onDateRangeEnd = (ctrl, val) => {
  const start = getDateStart(ctrl)
  const value = start ? `${start}~${val}` : val
  onFieldInput(ctrl, value)
  executeEvent(ctrl, 'onChange', { value, field: getFieldName(ctrl) })
}

const calcDays = (start, end) => {
  if (!start || !end) return 0
  const d1 = new Date(start), d2 = new Date(end)
  return Math.max(0, Math.round((d2 - d1) / 86400000))
}

// SubTable cell update
const onSubTableCellUpdate = (ctrl, rowIndex, col, value) => {
  const rows = getSubTableRows(ctrl)
  if (rows[rowIndex]) {
    const fieldName = col.props?.dataSourceField || col.props?.fieldName || col.props?.bindColumn || col.id
    rows[rowIndex][fieldName] = value
    emitFormUpdate()
  }
}

// File change
const onFileChange = (ctrl, files) => {
  const fileArr = Array.from(files || [])
  onFieldInput(ctrl, fileArr)
}

// Picker events
const onPickerEvent = (data) => {
  emit('update', data)
}

const openUserPicker = (ctrl) => { emit('picker', { type: 'user', control: ctrl }) }
const openDeptPicker = (ctrl) => { emit('picker', { type: 'dept', control: ctrl }) }
const openPositionPicker = (ctrl) => { emit('picker', { type: 'position', control: ctrl }) }

// Button click
const onButtonClick = (ctrl) => {
  const actionType = ctrl.props?.actionType || ctrl.props?.buttontype || ''
  if (actionType === 'add' || ctrl.props?.buttontype === 'Add') {
    // Find nearest subtable and add row
    const parent = findParentSubTable(ctrl)
    if (parent) addSubTableRow(parent)
  } else if (actionType === 'delete' || ctrl.props?.buttontype === 'Delete') {
    // Delete last row
    const parent = findParentSubTable(ctrl)
    if (parent) {
      const rows = getSubTableRows(parent)
      if (rows.length > (parent.props?.initRows || 1)) {
        rows.pop()
        emitFormUpdate()
      }
    }
  }
  emit('buttonClick', { control: ctrl, actionType })
}

const findParentSubTable = (ctrl) => {
  // Simple: find first SubTable in controls
  for (const c of props.controls || []) {
    if (c.type === 'SubTable') return c
  }
  return null
}

// Parse options string
const parseOptions = (optionsStr) => {
  if (!optionsStr) return []
  if (Array.isArray(optionsStr)) return optionsStr
  return String(optionsStr).split(',').map(s => s.trim()).filter(Boolean)
}

// Cell style
const getCellStyle = (ctrl) => {
  const colSpan = ctrl.props?.colSpan || 12
  const width = (colSpan / 12 * 100).toFixed(2) + '%'
  return { flex: `0 0 ${width}`, maxWidth: width }
}

// Visible controls (skip hidden)
const visibleControls = computed(() => {
  return (props.controls || []).filter(c => {
    if (c.props?.hidden) return false
    return true
  })
})

// Child update handler for containers
const onChildUpdate = (data) => {
  emit('update', data)
}

// Emit form update (for subtable)
const emitFormUpdate = () => {
  // Collect all subtable data
  const detail = {}
  for (const ctrl of props.controls || []) {
    if (ctrl.type === 'SubTable') {
      const tableName = (ctrl.props?.formbindtable || '').toLowerCase() || ctrl.id
      detail[tableName] = subTableRowsMap[ctrl.id] || []
    }
  }
  if (Object.keys(detail).length > 0) {
    emit('update', { detail })
  }
}

// Chinese capital conversion
const toChineseCapital = (num) => {
  const digits = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖']
  const units = ['', '拾', '佰', '仟']
  const str = String(num).replace(/[^0-9.]/g, '')
  if (!str) return ''
  const parts = str.split('.')
  let result = ''
  const intPart = parts[0]
  for (let i = 0; i < intPart.length; i++) {
    const d = parseInt(intPart[i])
    const pos = intPart.length - 1 - i
    if (d !== 0) {
      result += digits[d] + units[pos % 4]
      if (pos > 0 && pos % 4 === 0) result += '万'
    } else if (result && !result.endsWith('零')) {
      result += '零'
    }
  }
  if (!result) result = '零圆'
  if (parts[1]) {
    const d1 = parseInt(parts[1][0] || 0)
    const d2 = parseInt(parts[1][1] || 0)
    if (d1 > 0) result += digits[d1] + '角'
    if (d2 > 0) result += digits[d2] + '分'
  } else {
    result += '元整'
  }
  return result
}

// Character length calculation (all chars count as 1 for nvarchar)
function getCharLength(str) {
  if (!str) return 0
  let len = 0
  for (const ch of String(str)) {
    len += 1 // SQL Server nvarchar: each char = 1
  }
  return len
}

// Expression evaluator
function evaluateExpression(expr, fieldValues) {
  if (!expr) return 0
  let e = String(expr).trim()
  // Replace field references with actual values
  // Support patterns: [fieldId], fieldName, SUM(f1,f2,...), AVG(f1,f2,...), MAX(f1,f2,...), MIN(f1,f2,...)
  // Replace [fieldId] with values
  e = e.replace(/\[(\w+)\]/g, (_, key) => {
    const val = fieldValues[key] ?? 0
    return isNaN(Number(val)) ? 0 : Number(val)
  })
  // Handle SUM function
  e = e.replace(/SUM\s*\(([^)]+)\)/gi, (_, args) => {
    const fields = args.split(',').map(s => s.trim())
    return fields.reduce((sum, f) => {
      const v = fieldValues[f] ?? 0
      return sum + (isNaN(Number(v)) ? 0 : Number(v))
    }, 0)
  })
  // Handle AVG function
  e = e.replace(/AVG\s*\(([^)]+)\)/gi, (_, args) => {
    const fields = args.split(',').map(s => s.trim())
    const nums = fields.map(f => {
      const v = fieldValues[f] ?? 0
      return isNaN(Number(v)) ? 0 : Number(v)
    }).filter(n => n !== 0)
    return nums.length > 0 ? nums.reduce((a, b) => a + b, 0) / nums.length : 0
  })
  // Handle MAX function
  e = e.replace(/MAX\s*\(([^)]+)\)/gi, (_, args) => {
    const fields = args.split(',').map(s => s.trim())
    const nums = fields.map(f => {
      const v = fieldValues[f] ?? 0
      return isNaN(Number(v)) ? 0 : Number(v)
    })
    return Math.max(...nums, 0)
  })
  // Handle MIN function
  e = e.replace(/MIN\s*\(([^)]+)\)/gi, (_, args) => {
    const fields = args.split(',').map(s => s.trim())
    const nums = fields.map(f => {
      const v = fieldValues[f] ?? 0
      return isNaN(Number(v)) ? 0 : Number(v)
    })
    return Math.min(...nums, 0)
  })
  // Direct field reference (bare field name)
  e = e.replace(/\b(\w+)\b/g, (match) => {
    if (['SUM', 'AVG', 'MAX', 'MIN'].includes(match.toUpperCase())) return match
    const val = fieldValues[match] ?? 0
    return isNaN(Number(val)) ? 0 : Number(val)
  })
  try {
    // Safe eval with Function constructor
    const fn = new Function('return ' + e)
    return fn()
  } catch (ex) {
    return 0
  }
}

// Compute percentage for Input with ispercent
function computePercent(ctrl) {
  const totalExpr = ctrl.props?.total
  if (!totalExpr) return ''
  // Build field values from formData
  const fieldValues = {}
  const allControls = collectAllControls(props.controls || [])
  for (const c of allControls) {
    const fn = c.props?.dataSourceField || c.props?.fieldName || c.props?.name || c.id
    const val = parseFloat(props.formData[fn]) || 0
    fieldValues[fn] = val
    if (c.props?.fieldId) fieldValues[c.props.fieldId] = val
  }
  const result = evaluateExpression(totalExpr, fieldValues)
  if (isNaN(result)) return ''
  // Multiply by 100 and add % with 2 decimal places
  return (result * 100).toFixed(2) + '%'
}

// Compute expression result for InputNumber
function computeExpression(ctrl) {
  const expr = ctrl.props?.total
  if (!expr) return ''
  const fieldValues = {}
  const allControls = collectAllControls(props.controls || [])
  for (const c of allControls) {
    const fn = c.props?.dataSourceField || c.props?.fieldName || c.props?.name || c.id
    const val = parseFloat(props.formData[fn]) || 0
    fieldValues[fn] = val
    if (c.props?.fieldId) fieldValues[c.props.fieldId] = val
  }
  const result = evaluateExpression(expr, fieldValues)
  return isNaN(result) ? '' : Number(result).toFixed(ctrl.props?.decimal || 0)
}

// Format number with thousands separator
function formatNumber(val, decimals, useSeparator) {
  const n = Number(val)
  if (isNaN(n)) return val || ''
  const fixed = n.toFixed(decimals || 0)
  if (!useSeparator) return fixed
  const [intPart, decPart] = fixed.split('.')
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (decPart ? '.' + decPart : '')
}

// Collect all controls recursively
function collectAllControls(controls) {
  const result = []
  for (const c of controls || []) {
    result.push(c)
    if (c.children) result.push(...collectAllControls(c.children))
    if (c.type === 'Tabs' && c.props?.tabs) {
      for (const tab of c.props.tabs) {
        result.push(...collectAllControls(tab.children || []))
      }
    }
  }
  return result
}

// Execute event handler
function executeEvent(ctrl, eventName, context = {}) {
  const code = ctrl.props?.[eventName]
  if (!code || typeof code !== 'string' || !code.trim()) return
  try {
    // Create a safe execution context
    const fn = new Function('ctx', 'formData', 'ctrl', `
      try {
        ${code}
      } catch(e) {
        console.warn('Event execution error:', e)
      }
    `)
    fn(context, props.formData || {}, ctrl)
  } catch (e) {
    console.warn('Event parse error:', eventName, e)
  }
}

// Auto-browse on blur for Input with autobrowse
const onInputBlur = (ctrl, value) => {
  // Execute onBlur event
  executeEvent(ctrl, 'onBlur', { value, field: getFieldName(ctrl) })
  // Auto-browse: if autobrowse is enabled and there's a filter value
  if (ctrl.props?.autobrowse && value && value.trim()) {
    autoLookup(ctrl, value)
  }
}

// Auto lookup: search and auto-fill if single result
const autoLookup = async (ctrl, filterValue) => {
  const tableName = ctrl.props?.browsebindtable
  if (!tableName) return
  const showFields = ctrl.props?.browseshowfields || '*'
  const filterFormula = ctrl.props?.browsefilter || ''
  const orderBy = ctrl.props?.browseorderby || ''

  // Build filter: replace filter placeholders with value
  let whereClause = ''
  if (filterFormula) {
    whereClause = ' WHERE ' + filterFormula.replace(/\[FID\]/g, `'${filterValue.replace(/'/g, "''")}'`)
  } else {
    // Default: match against browsedisplaycolumn or first column
    whereClause = ` WHERE ${showFields.split(',')[0].trim()} LIKE '%${filterValue.replace(/'/g, "''")}%'`
  }

  const sql = `SELECT ${showFields} FROM ${tableName}${whereClause}${orderBy ? ' ORDER BY ' + orderBy : ''}`

  try {
    const res = await executeSql(sql)
    const data = res.data || res.Data || res || []
    const rows = Array.isArray(data) ? data : (data?.data || [])

    if (rows.length === 1) {
      // Single result: auto-fill
      const row = rows[0]
      const fieldName = getFieldName(ctrl)
      const dataMap = ctrl.props?.datamap || ''
      if (dataMap) {
        // Apply data mapping: "srcField=targetField,srcField2=targetField2"
        const mappings = dataMap.split(',').map(s => s.trim()).filter(Boolean)
        for (const map of mappings) {
          const [src, tgt] = map.split('=').map(s => s.trim())
          if (src && tgt && row[src] !== undefined) {
            emit('update', { [tgt]: row[src] })
          }
        }
      }
      // Fill current input with first column value
      const firstCol = showFields.split(',')[0].trim()
      if (row[firstCol] !== undefined) {
        onFieldInput(ctrl, String(row[firstCol]))
      }
    } else if (rows.length > 1) {
      // Multiple results: open lookup dialog
      lookupDialog.sourceControl = ctrl
      lookupDialog.columns = showFields.split(',').map(f => ({ field: f.trim(), label: f.trim() }))
      lookupDialog.data = rows
      lookupDialog.selectedRows = []
      lookupDialog.browseTable = tableName
      lookupDialog.browseShowFields = showFields
      lookupDialog.browseFilter = filterFormula
      lookupDialog.browseOrderby = orderBy
      lookupDialog.title = ctrl.props?.label || '选择数据'
      lookupDialog.showSearch = true
      lookupDialog.visible = true
    }
  } catch (e) {
    // Silent fail for auto-lookup
  }
}

// SubTable header rows (multi-level)
const getSubHeaderRows = (ctrl) => {
  const cols = ctrl?.props?.columns || []
  if (!cols.length) return [[{ text: '', colspan: 1, rowspan: 1, columns: [] }]]

  // Check if any column has headerLevel/headerGroup configured
  const hasMultiLevel = cols.some(c => {
    const lv = c.props?.headerLevel
    return lv && Number(lv) > 1
  }) || cols.some(c => c.props?.headerGroup)

  if (!hasMultiLevel) {
    // Simple single row
    const row = cols.map(col => ({
      text: col.props?.label || col.type || '',
      colspan: 1,
      rowspan: 1,
      columns: [col]
    }))
    return [row]
  }

  // Multi-level header (simplified for runtime)
  const levels = new Set()
  cols.forEach(c => {
    const lv = c.props?.headerLevel
    levels.add(lv ? Number(lv) : 1)
  })
  const maxLevel = Math.max(...levels, 1)
  const rows = []
  for (let lv = 1; lv <= maxLevel; lv++) rows.push([])

  // Group by headerGroup
  const groupMap = {}
  cols.forEach(col => {
    const group = col.props?.headerGroup || ''
    const lv = col.props?.headerLevel ? Number(col.props.headerLevel) : maxLevel
    if (group) {
      const parts = group.split('/').map(s => s.trim()).filter(Boolean)
      let path = ''
      parts.forEach((part, idx) => {
        const prevPath = path
        path = path ? path + '/' + part : part
        if (!groupMap[path]) {
          groupMap[path] = {
            text: part,
            level: idx + 1,
            parent: prevPath || null,
            children: [],
            cols: []
          }
          if (prevPath) groupMap[prevPath].children.push(path)
        }
      })
      groupMap[path].cols.push(col)
    }
  })

  // Build rows
  const rootGroups = Object.entries(groupMap).filter(([, v]) => !v.parent)
  const firstRow = []
  for (const [, rg] of rootGroups) {
    const colCount = rg.cols.length
    firstRow.push({ text: rg.text, colspan: colCount, rowspan: 1, level: 1, columns: rg.cols })
  }
  // Add ungrouped cols to first row
  cols.filter(c => !c.props?.headerGroup).forEach(col => {
    const lv = col.props?.headerLevel ? Number(col.props.headerLevel) : 1
    firstRow.push({ text: col.props?.label || col.type, colspan: 1, rowspan: 1, level: lv, columns: [col] })
  })
  rows[0] = firstRow

  // Second+ level rows
  for (let lv = 2; lv <= maxLevel; lv++) {
    const row = []
    for (const [, g] of Object.entries(groupMap)) {
      if (g.level === lv) {
        row.push({ text: g.text, colspan: g.cols.length, rowspan: maxLevel - lv + 1, level: lv, columns: g.cols })
      }
    }
    // Add ungrouped cols at their specific level
    cols.filter(c => !c.props?.headerGroup).forEach(col => {
      const clv = col.props?.headerLevel ? Number(col.props.headerLevel) : 1
      if (clv === lv) {
        row.push({ text: col.props?.label || col.type, colspan: 1, rowspan: maxLevel - lv + 1, level: lv, columns: [col] })
      }
    })
    if (row.length > 0) rows[lv - 1] = row
  }

  return rows.filter(r => r.length > 0)
}

// SubTable column width style
const getSubColWidthStyle = (col) => {
  if (!col) return {}
  const w = col.props?.width
  return w ? { width: w + 'px', minWidth: w + 'px', maxWidth: w + 'px' } : {}
}

// SubTable footer check
const getHasFooter = (ctrl) => {
  const cols = ctrl?.props?.columns || []
  return cols.some(c => c.props?.footerType && c.props.footerType !== '')
}

// SubTable column sum
const computeSubColSum = (ctrl, col) => {
  const rows = getSubTableRows(ctrl)
  const fieldName = col.props?.dataSourceField || col.props?.fieldName || col.props?.bindColumn || col.id
  return rows.reduce((sum, row) => {
    const v = parseFloat(row[fieldName])
    return sum + (isNaN(v) ? 0 : v)
  }, 0).toFixed(2)
}

// SubTable column average
const computeSubColAvg = (ctrl, col) => {
  const rows = getSubTableRows(ctrl)
  if (rows.length === 0) return '0.00'
  const fieldName = col.props?.dataSourceField || col.props?.fieldName || col.props?.bindColumn || col.id
  const sum = rows.reduce((s, row) => {
    const v = parseFloat(row[fieldName])
    return s + (isNaN(v) ? 0 : v)
  }, 0)
  return (sum / rows.length).toFixed(2)
}

// Footer button click
const onFooterButtonClick = (ctrl, col) => {
  const buttontype = col.props?.buttontype
  if (buttontype === 'Add') {
    addSubTableRow(ctrl)
  } else if (buttontype === 'Delete') {
    const rows = getSubTableRows(ctrl)
    if (rows.length > (ctrl.props?.initRows || 1)) {
      rows.pop()
      emitFormUpdate()
    }
  }
  emit('footerButtonClick', { control: ctrl, column: col, buttontype })
}

// Button label
const getButtonLabel = (ctrl) => {
  const labels = { Add: '新增', Delete: '删除', MoveUp: '上移', MoveDown: '下移', Browse: '浏览', BrowseCustom: '自定义浏览' }
  return labels[ctrl.props?.buttontype] || ctrl.props?.text || '按钮'
}

// ============ SubTableCell component ============
const SubTableCell = defineComponent({
  name: 'SubTableCell',
  props: {
    col: { type: Object, required: true },
    rowData: { type: Object, required: true },
    rowIndex: { type: Number, required: true },
    readonly: { type: Boolean, default: false }
  },
  emits: ['update'],
  setup(p, { emit }) {
    const getCellValue = () => {
      const fieldName = p.col.props?.dataSourceField || p.col.props?.fieldName || p.col.props?.bindColumn || p.col.id
      return p.rowData[fieldName] ?? ''
    }
    const onCellInput = (value) => {
      emit('update', value)
    }
    return () => {
      const p2 = p.col.props || {}
      const val = getCellValue()
      switch (p.col.type) {
        case 'Input': case 'Formula': case 'IdCard': case 'Hidden':
          return h('input', { type: 'text', class: 'sub-cell-input', value: val,
            placeholder: p2.placeholder || '', readonly: p.readonly, disabled: p2.disabled,
            maxlength: p2.maxLength, onInput: (e) => onCellInput(e.target.value) })
        case 'SerialNumber':
          return h('input', { type: 'text', class: 'sub-cell-input', value: val || '',
            placeholder: p2.placeholder || '提交后自动生成', readonly: true })
        case 'Textarea':
          return h('textarea', { class: 'sub-cell-input', value: val, rows: p2.rows || 2,
            readonly: p.readonly, disabled: p2.disabled,
            onInput: (e) => onCellInput(e.target.value) })
        case 'InputNumber': case 'Money':
          return h('input', { type: 'number', class: 'sub-cell-input', value: val,
            readonly: p.readonly, disabled: p2.disabled, min: p2.min, max: p2.max,
            step: p.col.type === 'Money' ? '0.01' : '1',
            onInput: (e) => onCellInput(e.target.value) })
        case 'DatePicker':
          return h('input', { type: p2.type === 'datetime' ? 'datetime-local' : 'date',
            class: 'sub-cell-input', value: val, readonly: p.readonly, disabled: p2.disabled,
            onInput: (e) => onCellInput(e.target.value) })
        case 'TimePicker':
          return h('input', { type: 'time', class: 'sub-cell-input', value: val,
            readonly: p.readonly, disabled: p2.disabled,
            onInput: (e) => onCellInput(e.target.value) })
        case 'Select': {
          const opts = parseOptions(p2.options)
          return h('select', { class: 'sub-cell-input', value: val, disabled: p.readonly || p2.disabled,
            onChange: (e) => onCellInput(e.target.value) }, [
            p2.prompt ? h('option', { value: '' }, p2.prompt) : null,
            ...opts.map(opt => h('option', { value: opt }, opt))
          ])
        }
        case 'Radio': {
          const opts = parseOptions(p2.options)
          return h('div', { class: 'sub-cell-radio' }, opts.map(opt =>
            h('label', { style: 'margin-right:8px;font-size:12px;' }, [
              h('input', { type: 'radio', name: 'sub_radio_' + p.col.id + '_' + p.rowIndex,
                value: opt, checked: val === opt, disabled: p.readonly || p2.disabled,
                onChange: () => onCellInput(opt) }), ' ' + opt
            ])
          ))
        }
        case 'Checkbox': {
          const opts = parseOptions(p2.options)
          return h('div', { class: 'sub-cell-checkbox' }, opts.map(opt =>
            h('label', { style: 'margin-right:8px;font-size:12px;' }, [
              h('input', { type: 'checkbox', value: opt,
                checked: val ? String(val).split(',').includes(opt) : false,
                disabled: p.readonly || p2.disabled,
                onChange: (e) => {
                  let arr = val ? String(val).split(',').filter(Boolean) : []
                  if (e.target.checked) { if (!arr.includes(opt)) arr.push(opt) }
                  else { arr = arr.filter(v => v !== opt) }
                  onCellInput(arr.join(','))
                }
              }), ' ' + opt
            ])
          ))
        }
        case 'Label':
          return h('span', { style: getLabelDivStyle(p.ctrl) }, p2.content || val || '')
        default:
          return h('input', { type: 'text', class: 'sub-cell-input', value: val,
            placeholder: p2.placeholder || '', readonly: p.readonly,
            onInput: (e) => onCellInput(e.target.value) })
      }
    }
  }
})

// ============ RenderControl component (recursive for containers) ============
const RenderControl = defineComponent({
  name: 'RenderControl',
  props: {
    ctrl: { type: Object, required: true },
    formData: { type: Object, default: () => ({}) },
    readonly: { type: Boolean, default: false }
  },
  emits: ['update', 'picker'],
  setup(p, { emit }) {
    const getFieldName2 = () => p.ctrl.props?.dataSourceField || p.ctrl.props?.fieldName || p.ctrl.props?.bindColumn || p.ctrl.props?.name || p.ctrl.id
    const getFieldValue2 = () => {
      const fn = getFieldName2()
      const tn = p.ctrl.props?.dataTargetTable || p.ctrl.props?.dataSourceTable || ''
      if (tn && p.formData?.main && p.formData.main[tn]) return p.formData.main[tn][fn] ?? ''
      return p.formData[fn] ?? p.ctrl.props?.defaultValue ?? ''
    }
    const onInput2 = (value) => {
      const fn = getFieldName2()
      const tn = p.ctrl.props?.dataTargetTable || p.ctrl.props?.dataSourceTable || ''
      if (tn) emit('update', { type: 'main', table: tn, field: fn, value })
      else emit('update', { [fn]: value })
    }
    const getCellStyle2 = () => {
      const colSpan = p.ctrl.props?.colSpan || 12
      const width = (colSpan / 12 * 100).toFixed(2) + '%'
      return { flex: `0 0 ${width}`, maxWidth: width }
    }
    const parseOptions2 = (optionsStr) => {
      if (!optionsStr) return []
      if (Array.isArray(optionsStr)) return optionsStr
      return String(optionsStr).split(',').map(s => s.trim()).filter(Boolean)
    }

    return () => {
      const ctrl = p.ctrl
      const p2 = ctrl.props || {}
      if (p2.hidden) return null

      const cellStyle = getCellStyle2()
      const cellChildren = []

      const renderInput = (type, extraProps = {}) => {
        const children = []
        if (p2.label) {
          const labelStyle = getCtrlLabelStyle(ctrl)
          children.push(h('label', { class: 'ctrl-label', style: labelStyle || undefined }, [
            p2.required ? h('span', { class: 'req' }, '*') : null,
            p2.label
          ]))
        }
        children.push(h('input', {
          type, class: 'ctrl-field',
          value: getFieldValue2(),
          placeholder: p2.placeholder || '',
          readonly: p.readonly || p2.readonly,
          disabled: p2.disabled,
          maxlength: p2.maxLength,
          min: p2.min, max: p2.max,
          step: extraProps.step || undefined,
          onInput: (e) => onInput2(e.target.value),
          ...extraProps
        }))
        if (p2.iscapital && getFieldValue2()) {
          children.push(h('span', { class: 'capital-hint' }, '大写：' + getFieldValue2()))
        }
        return h('div', { class: 'ctrl-wrap', style: cellStyle }, children)
      }

      switch (ctrl.type) {
        case 'Input': return renderInput('text')
        case 'Password': return renderInput('password')
        case 'PhoneNumber': return renderInput('tel', { placeholder: p2.placeholder || '请输入手机号' })
        case 'Email': return renderInput('email', { placeholder: p2.placeholder || '请输入邮箱' })
        case 'InputNumber': return renderInput('number', { step: Math.pow(10, -(p2.decimal || 0)) })
        case 'Money': {
          const ch = []
          if (p2.label) { const ls = getCtrlLabelStyle(ctrl); ch.push(h('label', { class: 'ctrl-label', style: ls || undefined }, [p2.required ? h('span', { class: 'req' }, '*') : null, p2.label])) }
          ch.push(h('div', { class: 'input-group' }, [
            h('span', { class: 'input-addon' }, '¥'),
            h('input', { type: 'number', class: 'ctrl-field money-input', value: getFieldValue2(),
              readonly: p.readonly || p2.readonly, disabled: p2.disabled, min: p2.min, max: p2.max, step: '0.01',
              onInput: (e) => onInput2(e.target.value) })
          ]))
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, ch)
        }
        case 'Textarea': {
          const ch = []
          if (p2.label) { const ls = getCtrlLabelStyle(ctrl); ch.push(h('label', { class: 'ctrl-label', style: ls || undefined }, [p2.required ? h('span', { class: 'req' }, '*') : null, p2.label])) }
          ch.push(h('textarea', { class: 'ctrl-field', value: getFieldValue2(), rows: p2.rows || 4,
            placeholder: p2.placeholder || '', readonly: p.readonly || p2.readonly, disabled: p2.disabled,
            onInput: (e) => onInput2(e.target.value) }))
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, ch)
        }
        case 'DatePicker': return renderInput(p2.type === 'datetime' ? 'datetime-local' : 'date')
        case 'TimePicker': return renderInput('time')
        case 'Select': {
          const ch = []
          if (p2.label) { const ls = getCtrlLabelStyle(ctrl); ch.push(h('label', { class: 'ctrl-label', style: ls || undefined }, [p2.required ? h('span', { class: 'req' }, '*') : null, p2.label])) }
          // 数据来源模式使用 selectOptionsMap，否则使用静态选项
          const opts = p2.sourceType === 'DataSource'
            ? (selectOptionsMap[ctrl.id] || []).map(o => ({ value: o.value, label: o.label }))
            : parseOptions2(p2.options).map(o => ({ value: o, label: o }))
          ch.push(h('select', { class: 'ctrl-field', value: getFieldValue2(),
            multiple: p2.multiple, disabled: p.readonly || p2.disabled,
            onChange: (e) => onInput2(e.target.value) }, [
            p2.prompt ? h('option', { value: '' }, p2.prompt) : null,
            ...opts.map(opt => h('option', { value: opt.value }, opt.label))
          ]))
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, ch)
        }
        case 'Radio': {
          const ch = []
          if (p2.label) { const ls = getCtrlLabelStyle(ctrl); ch.push(h('label', { class: 'ctrl-label', style: ls || undefined }, [p2.required ? h('span', { class: 'req' }, '*') : null, p2.label])) }
          const opts = parseOptions2(p2.options)
          ch.push(h('div', { class: 'ctrl-inline', class: { vertical: p2.direction === 'vertical' } }, opts.map(opt =>
            h('label', { class: 'ctrl-opt' }, [
              h('input', { type: 'radio', name: 'radio_' + ctrl.id, value: opt,
                checked: getFieldValue2() === opt, disabled: p.readonly || p2.disabled,
                onChange: () => onInput2(opt) }), ' ' + opt
            ])
          )))
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, ch)
        }
        case 'Checkbox': {
          const ch = []
          if (p2.label) { const ls = getCtrlLabelStyle(ctrl); ch.push(h('label', { class: 'ctrl-label', style: ls || undefined }, [p2.required ? h('span', { class: 'req' }, '*') : null, p2.label])) }
          const opts = parseOptions2(p2.options)
          const val = getFieldValue2()
          ch.push(h('div', { class: 'ctrl-inline', class: { vertical: p2.direction === 'vertical' } }, opts.map(opt =>
            h('label', { class: 'ctrl-opt' }, [
              h('input', { type: 'checkbox', value: opt,
                checked: val ? String(val).split(',').includes(opt) : false,
                disabled: p.readonly || p2.disabled,
                onChange: (e) => {
                  let arr = val ? String(val).split(',').filter(Boolean) : []
                  if (e.target.checked) { if (!arr.includes(opt)) arr.push(opt) }
                  else { arr = arr.filter(v => v !== opt) }
                  onInput2(arr.join(','))
                }
              }), ' ' + opt
            ])
          )))
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, ch)
        }
        case 'Switch': {
          const ch = []
          if (p2.label) { const ls = getCtrlLabelStyle(ctrl); ch.push(h('label', { class: 'ctrl-label', style: ls || undefined }, p2.label)) }
          const isOn = getFieldValue2() === '1' || getFieldValue2() === true
          ch.push(h('div', { class: 'switch-track' + (isOn ? ' on' : ''),
            onClick: () => { if (!p.readonly && !p2.disabled) onInput2(isOn ? '0' : '1') }
          }, [h('div', { class: 'switch-thumb' })]))
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, ch)
        }
        case 'Label': {
          const lblStyle = getLabelDivStyle(p.ctrl)
          const lblCls = p2.labelStyle === 'header' ? 'label-text label-header' : 'label-text'
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, [
            h('div', { class: lblCls, style: lblStyle }, p2.content || '')
          ])
        }
        case 'HyperLink': {
          const hlVal = getFieldValue2()
          const hlText = (p2.dataSourceField && hlVal) ? String(hlVal) : (p2.content || '链接')
          const hlHref = p2.href || '#'
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, [
            h('a', { href: hlHref, target: p2.target || '_blank', class: 'render-link' }, [hlText])
          ])
        }
        case 'RichText':
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, [
            h('textarea', { class: 'ctrl-field', rows: p2.rows || 3,
              value: getFieldValue2(),
              placeholder: p2.placeholder || '',
              onInput: (e) => {
                let val = e.target.value
                if (p2.maxLength && getCharLength(val) > p2.maxLength) {
                  let t = ''; let l = 0
                  for (const ch of val) { if (l >= p2.maxLength) break; t += ch; l += 1 }
                  val = t
                }
                onInput2(val)
              }
            })
          ])
        case 'Divider':
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, [h('hr', { class: 'dv' })])
        case 'SerialNumber':
          return renderInput('text', { readonly: true, value: getFieldValue2() || '', placeholder: p2.placeholder || '提交后自动生成' })
        case 'Formula':
          return renderInput('text', { readonly: true, placeholder: p2.formula || '计算结果' })
        case 'Hidden':
          return h('div', { style: 'display:none' }, [h('input', { type: 'hidden', value: getFieldValue2() })])
        case 'FileUpload': {
          const ch = []
          if (p2.label) { const ls = getCtrlLabelStyle(ctrl); ch.push(h('label', { class: 'ctrl-label', style: ls || undefined }, p2.label)) }
          ch.push(h('input', { type: 'file', class: 'ctrl-field',
            accept: p2.accept || '', multiple: p2.multiple,
            onChange: (e) => onInput2(Array.from(e.target.files)) }))
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, ch)
        }
        case 'ImageUpload': {
          const ch = []
          if (p2.label) { const ls = getCtrlLabelStyle(ctrl); ch.push(h('label', { class: 'ctrl-label', style: ls || undefined }, p2.label)) }
          ch.push(h('input', { type: 'file', class: 'ctrl-field',
            accept: 'image/*', multiple: p2.multiple,
            onChange: (e) => onInput2(Array.from(e.target.files)) }))
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, ch)
        }
        case 'UserPicker': case 'DeptPicker': case 'Position': {
          const icons = { UserPicker: 'fa-user-plus', DeptPicker: 'fa-building', Position: 'fa-briefcase' }
          const placeholders = { UserPicker: '请选择人员', DeptPicker: '请选择部门', Position: '请选择岗位' }
          const ch = []
          if (p2.label) { const ls = getCtrlLabelStyle(ctrl); ch.push(h('label', { class: 'ctrl-label', style: ls || undefined }, [p2.required ? h('span', { class: 'req' }, '*') : null, p2.label])) }
          ch.push(h('div', { class: 'picker-wrap' }, [
            h('input', { type: 'text', class: 'ctrl-field picker-input', readonly: true,
              value: getFieldValue2(), placeholder: p2.placeholder || placeholders[ctrl.type] || '' }),
            h('button', { type: 'button', class: 'picker-btn', disabled: p.readonly || p2.disabled,
              onClick: () => emit('picker', { type: ctrl.type.toLowerCase().replace('picker', ''), control: ctrl }) }, [
              h('i', { class: 'fas ' + (icons[ctrl.type] || 'fa-search') })
            ])
          ]))
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, ch)
        }
        case 'Tabs': {
          const activeTab = 0
          const ch = []
          ch.push(h('div', { class: 'tabs-bar' }, (p2.tabs || []).map((tab, ti) =>
            h('div', { key: ti, class: 'tab-tag' + (ti === activeTab ? ' active' : '') }, tab.title))))
          if (p2.tabs && p2.tabs[activeTab]) {
            ch.push(h('div', { class: 'tabs-content' }, (p2.tabs[activeTab].children || []).map(c =>
              h(RenderControl, { key: c.id, ctrl: c, formData: p.formData, readonly: p.readonly,
                onUpdate: (data) => emit('update', data), onPicker: (data) => emit('picker', data) }))))
          }
          return h('div', { class: 'ctrl-wrap', style: cellStyle }, ch)
        }
        default:
          return renderInput('text')
      }
    }
  }
})

// Init
initSubTableRows()

// Apply default values to formData on mount
onMounted(() => {
  // 加载 Select 动态选项
  for (const ctrl of props.controls) {
    if (ctrl.type === 'Select' && ctrl.props?.sourceType === 'DataSource') {
      loadSelectOptions(ctrl)
    }
    if (ctrl.type === 'TreeSelect' && ctrl.props?.selectbindtable) {
      loadTreeSelectOptions(ctrl)
    }
    // 递归检查子控件
    if (ctrl.children) {
      for (const child of ctrl.children) {
        if (child.type === 'Select' && child.props?.sourceType === 'DataSource') {
          loadSelectOptions(child)
        }
        if (child.type === 'TreeSelect' && child.props?.selectbindtable) {
          loadTreeSelectOptions(child)
        }
      }
    }
  }

  const applyDefaults = (controls) => {
    for (const ctrl of controls) {
      const p = ctrl.props || {}
      if (p.hidden) continue
      if (p.defaultValue && p.defaultValue !== '') {
        const fieldName = getFieldName(ctrl)
        const tableName = p.dataTargetTable || p.dataSourceTable || ''
        const resolved = resolveSysVar(String(p.defaultValue))
        // Only emit if formData doesn't already have a value for this field
        let currentVal = ''
        if (tableName && props.formData?.main && props.formData.main[tableName]) {
          currentVal = props.formData.main[tableName][fieldName] ?? ''
        } else {
          currentVal = props.formData[fieldName] ?? ''
        }
        if (!currentVal || currentVal === '') {
          if (tableName) {
            emit('update', { type: 'main', table: tableName, field: fieldName, value: resolved })
          } else {
            emit('update', { [fieldName]: resolved })
          }
        }
      }
      if (ctrl.children) applyDefaults(ctrl.children)
      if (ctrl.props?.tabs) {
        for (const tab of ctrl.props.tabs) {
          if (tab.children) applyDefaults(tab.children)
        }
      }
    }
  }
  applyDefaults(props.controls)
})

</script>

<style scoped>
.new-form-renderer {
  width: 100%;
  padding: 24px 28px;
  background: #f0f2f5;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0,0,0,.06);
}
.new-form-renderer.is-readonly .ctrl-field {
  background: #f9fafb;
  color: #6b7280;
}

.empty-controls {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 12px;
  color: #9ca3af;
}
.empty-controls i { font-size: 48px; }

.form-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 20px;
}
.form-cell {
  box-sizing: border-box;
}

.ctrl-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 14px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8ecf1;
  transition: border-color .2s, box-shadow .2s;
}
.ctrl-wrap:hover {
  border-color: #c5d3e0;
  box-shadow: 0 1px 4px rgba(0,0,0,.04);
}
.ctrl-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 3px;
  letter-spacing: .3px;
}
.req { color: #ef4444; margin-left: 1px; }

.ctrl-field {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 13px;
  outline: none;
  background: #fff;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Microsoft YaHei', sans-serif;
  height: 34px;
  transition: border-color 0.2s, box-shadow 0.2s;
  color: #1e293b;
}
.ctrl-field:focus {
  border-color: #1ab394;
  box-shadow: 0 0 0 2px rgba(26,179,148,.12);
}
.ctrl-field:read-only {
  background: #f8fafc;
  color: #64748b;
  cursor: default;
}
.ctrl-field:disabled {
  background: #f8fafc;
  color: #94a3b8;
  cursor: not-allowed;
}
textarea.ctrl-field {
  height: auto;
  min-height: 80px;
  resize: vertical;
  line-height: 1.5;
}

/* TreeSelect styles */
.treeselect-container {
  display: flex;
  align-items: center;
  gap: 8px;
}
.treeselect-icon {
  font-size: 16px;
  color: #1ab394;
}
.multi-treeselect {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}
.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px;
  min-height: 34px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  align-items: center;
}
.selected-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  background: #1ab394;
  color: white;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background .15s;
}
.selected-tag:hover {
  background: #15997c;
}
.selected-tag i {
  font-size: 10px;
}
.selected-tags .placeholder {
  color: #94a3b8;
  font-size: 13px;
  padding: 2px;
}

.input-group {
  display: flex;
  align-items: stretch;
}
.input-addon {
  display: flex;
  align-items: center;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  border-right: none;
  border-radius: 6px 0 0 6px;
  background: #f8fafc;
  font-size: 13px;
  color: #64748b;
}
.input-group .ctrl-field {
  border-radius: 0 6px 6px 0;
}

.ctrl-inline {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}
.ctrl-inline.vertical {
  flex-direction: column;
  gap: 6px;
}
.ctrl-opt {
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  color: #334155;
}
.ctrl-opt input { cursor: pointer; }

.switch-track {
  width: 40px;
  height: 22px;
  background: #cbd5e1;
  border-radius: 11px;
  position: relative;
  display: inline-block;
  cursor: pointer;
  transition: background 0.2s;
}
.switch-track.on { background: #1ab394; }
.switch-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 16px;
  height: 16px;
  background: #fff;
  border-radius: 50%;
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,.15);
}
.switch-track.on .switch-thumb { left: 21px; }

.picker-wrap {
  display: flex;
  align-items: stretch;
  gap: 0;
}
.picker-input {
  border-radius: 6px 0 0 6px;
  border-right: none;
  flex: 1;
}
.picker-btn {
  border: 1px solid #e2e8f0;
  border-radius: 0 6px 6px 0;
  background: #f8fafc;
  padding: 0 10px;
  cursor: pointer;
  color: #64748b;
  font-size: 13px;
  display: flex;
  align-items: center;
  transition: all .15s;
}
.picker-btn:hover { background: #e2e8f0; color: #1ab394; }
.picker-btn:disabled { cursor: not-allowed; opacity: 0.5; }

.render-link {
  color: #1ab394;
  text-decoration: none;
  font-size: 13px;
}
.render-link:hover { text-decoration: underline; }

.dv {
  border: none;
  border-top: 2px solid #e2e8f0;
  margin: 4px 0;
}

.btn-render {
  padding: 6px 18px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  font-size: 13px;
  cursor: pointer;
  background: #fff;
  color: #334155;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Microsoft YaHei', sans-serif;
  font-weight: 500;
  transition: all .15s;
}
.btn-render:hover { border-color: #cbd5e1; box-shadow: 0 1px 3px rgba(0,0,0,.06); }
.btn-render.btn-primary { background: #1ab394; color: #fff; border-color: #1ab394; }
.btn-render.btn-primary:hover { background: #159b80; }
.btn-render.btn-danger { background: #ef4444; color: #fff; border-color: #ef4444; }
.btn-render.sz-small { padding: 4px 10px; font-size: 12px; }
.btn-render.sz-large { padding: 8px 24px; font-size: 14px; }

.container-label {
  font-size: 13px;
  color: #334155;
  font-weight: 600;
  margin-bottom: 6px;
}
.container-body {
  border: 1px dashed #e2e8f0;
  border-radius: 6px;
  min-height: 30px;
}
fieldset {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 10px;
}
legend {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  padding: 0 6px;
}

/* Tabs - modern green theme */
.tabs-wrap { width: 100%; }
.tabs-bar { display: flex; align-items: stretch; background: linear-gradient(135deg, #e8f5f0, #e0f2f1); border-radius: 8px 8px 0 0; overflow: hidden; border: 1px solid #b2dfdb; border-bottom: none; }
.tab-tag { padding: 8px 20px; font-size: 13px; cursor: pointer; color: #4a5568; background: transparent; flex: 1; text-align: center; white-space: nowrap; transition: all 0.2s; font-weight: 500; }
.tab-tag:hover { background: rgba(26,179,148,.08); }
.tab-tag.active { background: #1ab394; color: #fff; font-weight: 600; border-radius: 8px 8px 0 0; box-shadow: 0 2px 4px rgba(26,179,148,.2); }
.tabs-content { border: 1px solid #b2dfdb; border-radius: 0 0 8px 8px; padding: 14px; background: #fff; min-height: 60px; }

/* SubTable */
.sub-table-wrap {
  width: 100%;
}
.sub-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Microsoft YaHei', sans-serif;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}
.sub-table th, .sub-table td {
  border: 1px solid #e2e8f0;
  padding: 6px 10px;
  vertical-align: middle;
  text-align: left;
  min-height: 28px;
  line-height: 1.5;
}
.sub-table th {
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  font-weight: 600;
  color: #475569;
  text-align: center;
  white-space: nowrap;
  padding-left: 12px;
}
.sub-cell-input {
  width: 100%;
  border: none;
  background: none;
  color: #334155;
  font-size: 13px;
  padding: 2px 4px;
  margin: 0;
  outline: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Microsoft YaHei', sans-serif;
  box-sizing: border-box;
}
.sub-cell-input:focus { border-bottom: 2px solid #1ab394; }
.sub-cell-radio, .sub-cell-checkbox {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.sub-cell-radio label, .sub-cell-checkbox label {
  font-size: 12px;
  color: #334155;
  cursor: pointer;
}
.row-add-btn {
  background: #1ab394;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 5px 14px;
  font-size: 12px;
  cursor: pointer;
}
.row-add-btn:hover { background: #159b80; }
.row-add-btn-inline {
  background: #1ab394;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 3px 8px;
  font-size: 11px;
  cursor: pointer;
  margin-right: 4px;
}
.row-add-btn-inline:hover { background: #159b80; }
.row-add-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.row-del-btn {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: 14px;
  padding: 3px 8px;
  border-radius: 4px;
  transition: background .15s;
}
.row-del-btn:hover { background: #fef2f2; }
.row-del-btn:disabled { cursor: not-allowed; opacity: 0.5; }

.sub-footer-cell { background: #fefce8 !important; text-align: center; padding: 6px 10px; }
.footer-text { font-size: 12px; color: #92400e; font-weight: 600; }
.footer-btn { padding: 3px 12px; border-radius: 4px; border: 1px solid #e2e8f0; font-size: 12px; cursor: pointer; background: #fff; font-weight: 500; }
.footer-btn.btn-primary { background: #1ab394; color: #fff; border-color: #1ab394; }
.footer-btn.btn-danger { background: #ef4444; color: #fff; border-color: #ef4444; }

/* DateRange combined unit */
.date-range-unit {
  display: flex;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
  transition: border-color .2s;
}
.date-range-unit:focus-within {
  border-color: #1ab394;
  box-shadow: 0 0 0 2px rgba(26,179,148,.12);
}
.dr-input { border: none !important; border-radius: 0 !important; box-shadow: none !important; flex: 1; min-width: 0; height: 34px; padding: 7px 10px; font-size: 13px; }
.dr-input:focus { outline: none; background: #f8fafc; }
.dr-sep { padding: 0 8px; color: #94a3b8; font-size: 14px; flex-shrink: 0; user-select: none; }
.dr-interval {
  font-size: 11px;
  color: #1ab394;
  background: #e8f5f0;
  padding: 3px 8px;
  border-radius: 4px;
  white-space: nowrap;
  flex-shrink: 0;
  margin-right: 4px;
  font-weight: 600;
}

.capital-hint {
  font-size: 11px;
  color: #059669;
  margin-top: 3px;
  font-weight: 500;
}
.percent-hint {
  font-size: 11px;
  color: #8b5cf6;
  margin-top: 3px;
  font-weight: 500;
}
.num-hint {
  font-size: 11px;
  color: #64748b;
  margin-top: 3px;
}

.layout-table {
  width: 100%;
  border-collapse: collapse;
}
.layout-table td {
  border: 1px dashed #e2e8f0;
  padding: 8px;
  vertical-align: top;
  min-height: 40px;
}
.layout-cell { min-height: 30px; }

/* RichText editor */
.richtext-editor {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}
.richtext-editor.is-readonly .rt-toolbar { display: none; }
.rt-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 8px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  align-items: center;
}
.rt-btn {
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  color: #475569;
  transition: all .15s;
}
.rt-btn:hover { background: #e2e8f0; }
.rt-sep { color: #cbd5e1; margin: 0 4px; font-size: 12px; }
.rt-select {
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 3px 6px;
  font-size: 12px;
  background: #fff;
  outline: none;
  height: 28px;
}
.rt-color {
  width: 28px; height: 28px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 2px;
  cursor: pointer;
  background: #fff;
}
.rt-content {
  padding: 10px 12px;
  min-height: 150px;
  outline: none;
  font-size: 13px;
  line-height: 1.6;
  color: #334155;
}
.rt-char-count {
  padding: 4px 10px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  font-size: 11px;
  color: #94a3b8;
  text-align: right;
}
.rt-over-limit { color: #ef4444; }
/* Label 文本控件 */
.label-text { word-break: break-all; }
.label-header {
  background: rgb(16, 185, 129);
  color: #fff;
  text-align: center;
  font-size: 16px;
  line-height: 32px;
  border-top-left-radius: 20px;
  border-bottom-right-radius: 20px;
  border-radius: 0;
  box-shadow: 20px 0px 0px #d1fae5;
  padding: 0 8px;
}
</style>
